import { useAudioTrackOptions, useDestroy, usePlayerContext } from "@videojs/react";
import type { ThumbnailImage } from "@videojs/core";
import { HlsJsMedia } from "@videojs/core/dom/media/hls-js";
import type {
  PlaybackAudioTrack,
  PlaybackVideoCodec,
  PlaybackVideoRendition
} from "@recital-master/shared";
import * as Sentry from "@sentry/react-native";
import { Events } from "hls.js";
import { Player, PlayerSkin } from "./skin/PlayerSkin";
import type { VideoSurfaceSource } from "./videoSource";
import {
  hlsAudioPreferenceForId,
  logicalAudioTrackIdForMediaIndex,
  mapWebAudioTrackState,
  mediaAudioTrackIndexForLogicalId,
  reportedWebAudioTrackId,
  shouldRepreparePreloadedAudioSlot,
  webHlsUriWithAudioPreference
} from "./webAudioTrackPreference";
import { mirrorWebQueueVolume } from "./webQueueVolume";
import {
  mirrorWebQueuePlaybackRate,
  normalizeWebPlaybackRate
} from "./webQueuePlaybackRate";
import {
  sharedWebQueueBandwidthEstimate,
  WEB_HLS_COLD_START_BANDWIDTH_BPS
} from "./webQueueBandwidth";
import { webHlsMediaConfig } from "./webHlsMediaConfig";
import { isAudibleAutoplayPolicyRejection } from "./webPlaybackInputPolicy";
import { parseStoryboardVtt } from "../storyboardThumbnails";
import {
  isWebMediaNaturalEndPause,
  isWebKitWirelessPlaybackActive,
  primeWebKitAirPlaySource,
  replaceConnectedWebKitAirPlaySource,
  requestWebKitPlaybackTarget,
  selectWebAudioTrack,
  setWebMediaSourceForPlaybackTarget,
  shouldReplaceIncompleteWebKitAirPlayAudioSource,
  shouldReplaceRoutedWebKitAirPlaySource,
  webKitAirPlayAudioTrackRetryDelayMs,
  webKitAirPlayNaturalAdvanceDelayMs,
  webKitWirelessAudioTracks,
  webAudioTrackArray,
  webAudioTracksForMedia,
  webKitPlaybackTargetAvailability,
  type WebAudioTrackLike,
  type WebKitPlaybackTargetAvailability
} from "./webRemotePlayback";
import {
  shouldBeginNaturalEndEnvelope,
  TRANSPORT_DECLICK_MS
} from "./transportEnvelope";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type SyntheticEvent
} from "react";

const PICTURE_IN_PICTURE_TRANSFER_TIMEOUT_MS = 3_000;

function logWebQueue(event: string, details: Record<string, unknown>) {
  console.log(`[web-queue] ${event} ${JSON.stringify(details)}`);
}

function webQueueGenerationLabel(generation: string | number | null) {
  return typeof generation === "string" && generation.startsWith("prewarm:")
    ? "prewarm"
    : generation;
}

function playbackVideoCodec(codec: string | undefined): PlaybackVideoCodec {
  const prefix = codec?.toLowerCase().slice(0, 4);
  if (prefix === "avc1" || prefix === "avc3") return "h264";
  if (prefix === "hvc1" || prefix === "hev1") return "hevc";
  if (prefix === "av01") return "av1";
  if (prefix === "vp09" || prefix === "vp9") return "vp9";
  return "unknown";
}

function sameVideoRendition(
  left: PlaybackVideoRendition | null,
  right: PlaybackVideoRendition | null
) {
  return left?.width === right?.width &&
    left?.height === right?.height &&
    left?.codec === right?.codec;
}

type WebQueueSlot = {
  /** Temporary safety mute; never feed this back into the user's mute intent. */
  audioGuardMuted: boolean;
  audioTrackDescriptors: PlaybackAudioTrack[];
  bandwidthEstimateInherited: boolean;
  bandwidthEstimateSeeded: number;
  danceId: string | null;
  /** Chromium-only sample-accurate transport envelope. */
  audioSourceNode: MediaElementAudioSourceNode | null;
  element: HTMLVideoElement | null;
  generation: string | number | null;
  media: HlsJsMedia;
  nativeHlsSourceOwned: boolean;
  pictureInPictureBinding: AbortController | null;
  preferredAudioTrackCommitted: string | null;
  ready: boolean;
  requiresPreferredAudioBeforeAudible: boolean;
  trackBinding: AbortController | null;
  transportGainNode: GainNode | null;
  transportGainGeneration: number;
  unbindEngineAudioTracks: (() => void) | null;
  unbindEngineVideoRendition: (() => void) | null;
  uri: string | null;
  videoRendition: PlaybackVideoRendition | null;
};

type AirPlayAudioSourceReplacement = {
  element: HTMLVideoElement;
  generation: string | number | null;
  position: number;
  uri: string;
};

function supportsWebAudioTransportEnvelope() {
  if (typeof window === "undefined" || typeof AudioContext === "undefined") {
    return false;
  }
  // Safari owns AirPlay routing through the media element. Moving that element
  // behind a Web Audio graph can make the receiver route unavailable or silent.
  // Chromium (desktop and Android) has no such HTML-media AirPlay path; Google
  // Cast is app-controlled separately and never consumes this local graph.
  const userAgent = navigator.userAgent;
  const isAppleWebKit = /AppleWebKit/i.test(userAgent) && !/(Chrome|Chromium|Edg|CriOS)/i.test(userAgent);
  const isAppleMobile = /iPad|iPhone|iPod/i.test(userAgent);
  return !isAppleWebKit && !isAppleMobile;
}

function hasExternalPlaybackRoute(element: HTMLVideoElement) {
  return Boolean(
    isWebKitWirelessPlaybackActive(element) ||
      ("remote" in element && element.remote?.state === "connected")
  );
}

function normalizedWebAudioTracks(tracks: Iterable<WebAudioTrackLike>) {
  return Array.from(tracks).map((track, index) => ({
    enabled: track.enabled,
    id: track.id || String(index),
    label: track.label
  }));
}

function waitForPictureInPictureReady(element: HTMLVideoElement): Promise<void> {
  if (element.readyState > HTMLMediaElement.HAVE_NOTHING) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const finish = (error?: Error) => {
      if (timeout) clearTimeout(timeout);
      controller.abort();
      if (error) reject(error);
      else resolve();
    };
    timeout = setTimeout(
      () => finish(new Error("Incoming PiP video did not load metadata in time.")),
      PICTURE_IN_PICTURE_TRANSFER_TIMEOUT_MS
    );
    element.addEventListener("loadedmetadata", () => finish(), {
      once: true,
      signal: controller.signal
    });
    element.addEventListener(
      "error",
      () => finish(new Error("Incoming PiP video failed to load.")),
      { once: true, signal: controller.signal }
    );
  });
}

function ActiveMediaBridge({ media }: { media: HlsJsMedia }) {
  const { setMedia } = usePlayerContext();

  useLayoutEffect(() => {
    setMedia(media);
    return () => setMedia((current) => (current === media ? null : current));
  }, [media, setMedia]);

  return null;
}

function useWebQueueSlotLifecycle(
  slot: WebQueueSlot,
  bindAudioTracks: (slot: WebQueueSlot) => void
) {
  const handleLoadStart = useCallback(() => bindAudioTracks(slot), [bindAudioTracks, slot]);
  useDestroy(
    slot.media,
    () => {
      slot.media.addEventListener("loadstart", handleLoadStart);
      slot.media.preload = "auto";
    },
    () => {
      slot.media.removeEventListener("loadstart", handleLoadStart);
      slot.pictureInPictureBinding?.abort();
      slot.trackBinding?.abort();
      slot.unbindEngineAudioTracks?.();
      slot.unbindEngineVideoRendition?.();
    }
  );
}

export type VideoSurfaceAudioTrack = Pick<PlaybackAudioTrack, "id" | "label">;
export type VideoSurfaceContentFit = "contain" | "cover";

export type VideoSurfaceSlotDiagnostics = {
  advertisedBitrateBps: number | null;
  bandwidthEstimateBps: number | null;
  bufferAheadSeconds: number;
  currentTimeSeconds: number;
  danceId: string | null;
  mediaErrorCode: number | null;
  paused: boolean;
  playbackEngine: "hls.js" | "native HLS";
  readyState: number;
  rendition: PlaybackVideoRendition | null;
};

export type VideoSurfaceDiagnostics = {
  active: VideoSurfaceSlotDiagnostics | null;
  standby: VideoSurfaceSlotDiagnostics | null;
};

export type VideoSurfaceHandle = {
  readonly availableAudioTracks: VideoSurfaceAudioTrack[];
  /** Authoritative media-clock position for lifecycle and telemetry boundaries. */
  readonly currentTime: number;
  /** Live local-pipeline facts used by the user-visible playback stats panel. */
  readonly diagnostics: VideoSurfaceDiagnostics;
  readonly selectedAudioTrack: string | null;
  seek: (seconds: number) => void;
  setPlaybackRate: (rate: number) => void;
  setAudioTrack: (id: string) => void;
  /** Promote the already-preloaded successor on the same platform player. */
  advanceToNext: () => Promise<boolean>;
  /** Promote the nearest item from the platform player's bounded warm history. */
  advanceToPrevious: () => Promise<boolean>;
  beginScrubbing: () => void;
  endScrubbing: (resumePlaying?: boolean) => void;
  /** Immediately pause the underlying platform player before React confirmation. */
  pausePlayback: () => void;
  /** Silence local output before replacing an item that is not in the queue. */
  prepareForSourceChange: () => Promise<void>;
  playAfterScrubbing: () => void;
  /** Re-assert controlled playback after a native route-picker handoff. */
  resumePlayback: () => void;
  /** Terminal teardown: stop playback and discard current and queued media. */
  terminatePlayback: () => void;
  requestPictureInPicture: () => void | Promise<unknown>;
  /** Web only: open Safari's playback-target picker from the current gesture. */
  requestRemotePlayback?: () => boolean;
};

export type VideoSurfaceProps = {
  src: VideoSurfaceSource | null;
  /** Successor kept warm in the platform player's bounded queue. */
  nextSrc?: VideoSurfaceSource | null;
  /** Predecessor identity used by native players to adopt retained history. */
  previousSrc?: VideoSurfaceSource | null;
  /** Second predecessor kept prepared by native players for repeated Back. */
  previousPreviousSrc?: VideoSurfaceSource | null;
  /** Reload the current URL without replacing the surrounding player surface. */
  sourceGeneration?: string | number;
  playing: boolean;
  muted?: boolean;
  /** Stable catalog feed ID to apply while preparing current and queued HLS. */
  preferredAudioTrack?: string | null;
  /** Controlled playback speed shared by app chrome and the Video.js skin. */
  playbackRate?: number;
  initialPosition?: number;
  /** Complete frame (`contain`) or edge-to-edge crop (`cover`). */
  contentFit?: VideoSurfaceContentFit;
  /** Native only: keep the platform video layer composed but visually hidden. */
  concealed?: boolean;
  /** Web only: show the Video.js skin. `?chrome=app` on the watch route
   * turns it off to preview the app chrome (what iOS/Android ship) in a
   * browser. */
  showControls?: boolean;
  /** Web only: top-left overlay above the video, fading with the controls.
   * Native shows this in PlayerChrome instead. */
  eyebrow?: string;
  title?: string;
  /** Web only: keep the title visible while the shared loading veil is up. */
  titlePinned?: boolean;
  /** Web only: WebVTT storyboard URL — enables hover scrub previews. */
  thumbnailsVtt?: string;
  onTime?: (currentSeconds: number, durationSeconds: number) => void;
  /** Exact pre-seek media position, before the playhead discontinuity. */
  onSeekStart?: (currentSeconds: number) => void;
  /** `true` when the platform player already promoted a ready successor. */
  onEnded?: (nativeQueuedAdvance?: boolean) => void;
  onError?: (message: string) => void;
  onFirstFrame?: () => void;
  /** A preloaded successor became current without replacing the player. */
  onQueuedItemAdopted?: () => void;
  /** Native only: a promoted successor proved it still needs media data. */
  onTransitionLoading?: () => void;
  /** Reports whether the platform player is currently routed through AirPlay. */
  onExternalPlaybackChange?: (active: boolean) => void;
  /** Web only: reports whether Safari exposes its AirPlay target picker. */
  onRemotePlaybackAvailabilityChange?: (
    availability: WebKitPlaybackTargetAvailability
  ) => void;
  /** Native: reports explicit Picture-in-Picture presentation ownership. */
  onPictureInPictureChange?: (active: boolean) => void;
  onTogglePlay?: () => void;
  /** App-owned recital queue navigation shown by the web player skin. */
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPlayingChange?: (playing: boolean) => void;
  /** The effective web mute state, including autoplay-policy fallback. */
  onMutedChange?: (muted: boolean) => void;
  /** The active media element adopted a new playback speed. */
  onPlaybackRateChange?: (rate: number) => void;
  /** Web only: mirrors the same Video.js controls state used by the title overlay. */
  onControlsVisibilityChange?: (visible: boolean) => void;
  /** Web only: an app-owned overlay is being hovered or keyboard-focused. */
  controlsPinned?: boolean;
  /** Native audio-rendition state for the app-owned settings surface. */
  onAvailableAudioTracksChange?: (tracks: VideoSurfaceAudioTrack[]) => void;
  onSelectedAudioTrackChange?: (id: string | null) => void;
  /** Active rendered video only; hidden queue preloads are never published. */
  onVideoRenditionChange?: (
    danceId: string | null,
    rendition: PlaybackVideoRendition | null
  ) => void;
  /** Shared custom-fullscreen presentation state. */
  isFullscreen?: boolean;
  /** Fullscreen the owner container so every custom overlay remains visible. */
  onFullscreen?: () => void;
};

function AppChromeAudioTrackBridge({
  onChange
}: {
  onChange: (
    tracks: VideoSurfaceAudioTrack[],
    selectedId: string | null,
    select: ((id: string) => void) | null
  ) => void;
}) {
  const audioTrack = useAudioTrackOptions();
  const signature =
    audioTrack?.options.map((option) => `${option.value}:${option.label}`).join("|") ?? "";
  const selectedId = audioTrack?.value || null;
  const select = audioTrack?.setValue ?? null;

  useEffect(() => {
    onChange(
      audioTrack?.options.map((option) => ({
        id: option.value,
        label: option.label
      })) ?? [],
      selectedId,
      select
    );
  }, [audioTrack?.state.availability, onChange, select, selectedId, signature]);

  return null;
}

function ControlsContinuityBridge({
  controlsPinned,
  onChange,
  setSourceRevealSuppressed,
  stableControlsVisibleRef,
  sourceGeneration,
  sourcePlaybackStarted,
  sourceRevealSuppressed
}: {
  controlsPinned: boolean;
  onChange?: (visible: boolean) => void;
  setSourceRevealSuppressed: (suppressed: boolean) => void;
  stableControlsVisibleRef: { current: boolean };
  sourceGeneration: string | number;
  sourcePlaybackStarted: boolean;
  sourceRevealSuppressed: boolean;
}) {
  const player = Player.usePlayer();
  const controlsVisible = Player.usePlayer((state) => state.controlsVisible);
  const previousGenerationRef = useRef(sourceGeneration);
  const sourceChanged = previousGenerationRef.current !== sourceGeneration;

  useLayoutEffect(() => {
    if (!sourceChanged) return;
    previousGenerationRef.current = sourceGeneration;
    setSourceRevealSuppressed(!stableControlsVisibleRef.current);
  }, [setSourceRevealSuppressed, sourceChanged, sourceGeneration, stableControlsVisibleRef]);

  useEffect(() => {
    if (!sourceChanged && !sourceRevealSuppressed) {
      stableControlsVisibleRef.current = controlsVisible || controlsPinned;
    }
    onChange?.(controlsVisible && (!sourceRevealSuppressed || controlsPinned));
  }, [
    controlsPinned,
    controlsVisible,
    onChange,
    sourceChanged,
    sourceRevealSuppressed,
    stableControlsVisibleRef
  ]);

  useEffect(() => {
    if (!sourceRevealSuppressed || !sourcePlaybackStarted) return;
    if (player.state.controlsVisible) {
      player.toggleControls();
      return;
    }
    setSourceRevealSuppressed(false);
  }, [player, setSourceRevealSuppressed, sourcePlaybackStarted, sourceRevealSuppressed]);

  return null;
}

/** Dance name + style eyebrow, top-left, shown/hidden with the controls.
 * `large` scales the typography for fullscreen viewing distance. */
function TitleOverlay({
  eyebrow,
  title,
  pinned = false,
  suppressed = false,
  large = false
}: {
  eyebrow?: string;
  title?: string;
  pinned?: boolean;
  suppressed?: boolean;
  large?: boolean;
}) {
  const controlsVisible = Player.usePlayer((state) => state.controlsVisible);
  return (
    <div
      data-testid="player-title-scrim"
      style={{
        ...overlayStyle,
        ...(large ? overlayLargeStyle : null),
        opacity: suppressed ? 0 : pinned || controlsVisible ? 1 : 0
      }}
    >
      {eyebrow ? (
        <span style={{ ...eyebrowStyle, ...(large ? eyebrowLargeStyle : null) }}>
          {eyebrow}
        </span>
      ) : null}
      {title ? (
        <span style={{ ...overlayTitleStyle, ...(large ? overlayTitleLargeStyle : null) }}>
          {title}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Browser playback surface: Video.js 10 (`@videojs/react` — see AGENTS.md)
 * owns HLS playback; controls/scrubbing/a11y come from our ejected skin in
 * ./skin/PlayerSkin (structure and styles are ours to edit directly —
 * don't fight it with overrides).
 * Native uses expo-video with the app's own chrome — see VideoSurface.tsx.
 */
export const VideoSurface = forwardRef<VideoSurfaceHandle, VideoSurfaceProps>(
  function VideoSurface(
    {
      src,
      nextSrc,
      sourceGeneration = 0,
      playing,
      muted = false,
      preferredAudioTrack = null,
      playbackRate,
      initialPosition,
      contentFit = "contain",
      showControls = true,
      eyebrow,
      title,
      titlePinned = false,
      thumbnailsVtt,
      onTime,
      onSeekStart,
      onEnded,
      onError,
      onFirstFrame,
      onQueuedItemAdopted,
      onExternalPlaybackChange,
      onTogglePlay,
      onPrevious,
      onNext,
      hasPrevious = false,
      hasNext = false,
      onPlayingChange,
      onMutedChange,
      onPlaybackRateChange,
      onControlsVisibilityChange,
      controlsPinned = false,
      onAvailableAudioTracksChange,
      onSelectedAudioTrackChange,
      onVideoRenditionChange,
      onRemotePlaybackAvailabilityChange,
      isFullscreen = false,
      onFullscreen
    },
    ref
  ) {
    const sourceUri = src?.uri ?? null;
    const nextSourceUri = nextSrc?.uri ?? null;
    const sourceArtwork = src?.metadata.artwork;
    const videoRef = useRef<HTMLVideoElement>(null);
    const lastReportedTimeRef = useRef(Math.max(0, initialPosition ?? 0));
    useLayoutEffect(() => {
      lastReportedTimeRef.current = Math.max(0, initialPosition ?? 0);
    }, [initialPosition, sourceGeneration, sourceUri]);
    const [slots] = useState<[WebQueueSlot, WebQueueSlot]>(() => {
      const createSlot = (): WebQueueSlot => ({
        audioGuardMuted: false,
        audioTrackDescriptors: [],
        bandwidthEstimateInherited: false,
        bandwidthEstimateSeeded: WEB_HLS_COLD_START_BANDWIDTH_BPS,
        danceId: null,
        audioSourceNode: null,
        element: null,
        generation: null,
        media: new HlsJsMedia(),
        nativeHlsSourceOwned: false,
        pictureInPictureBinding: null,
        preferredAudioTrackCommitted: null,
        ready: false,
        requiresPreferredAudioBeforeAudible: false,
        trackBinding: null,
        transportGainNode: null,
        transportGainGeneration: 0,
        unbindEngineAudioTracks: null,
        unbindEngineVideoRendition: null,
        uri: null,
        videoRendition: null
      });
      return [createSlot(), createSlot()];
    });
    const activeSlotRef = useRef<0 | 1>(0);
    const audioContextRef = useRef<AudioContext | null>(null);
    const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
    const awaitingPropAdoptionUriRef = useRef<string | null>(null);
    const remoteRouteReplacementUriRef = useRef<string | null>(null);
    const pictureInPictureElementRef = useRef<HTMLVideoElement | null>(null);
    const nextSourceUriRef = useRef(nextSourceUri);
    const playingIntentRef = useRef(playing);
    const mutedIntentRef = useRef(muted);
    const volumeIntentRef = useRef<number | null>(null);
    const playbackRateIntentRef = useRef(
      normalizeWebPlaybackRate(playbackRate ?? 1)
    );
    const sessionBandwidthEstimateRef = useRef(
      WEB_HLS_COLD_START_BANDWIDTH_BPS
    );
    const sessionHasMeasuredBandwidthRef = useRef(false);
    // The first active element is born with `autoplay`. Let the browser own
    // that startup request exactly as HlsJsVideo does; issuing element.play()
    // while HLS is still at HAVE_NOTHING cancels the working native path in
    // Chromium. Queue promotions and later controlled resumes remain
    // imperative because their elements already exist.
    const nativeAutoplaySourceRef = useRef<string | null>(null);
    nextSourceUriRef.current = nextSourceUri;
    playingIntentRef.current = playing;
    playbackRateIntentRef.current = normalizeWebPlaybackRate(playbackRate ?? 1);
    const pendingSeekRef = useRef<number | undefined>(initialPosition);
    const seekEnvelopePendingRef = useRef(false);
    const seekEnvelopeSilencedRef = useRef(false);
    const seekFadePromiseRef = useRef<Promise<void> | null>(null);
    const seekReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const firstFrameSrcRef = useRef<string | null>(null);
    const firstFrameFiredRef = useRef(false);
    const frameCallbackIdRef = useRef<number | null>(null);
    const naturalEndEnvelopeSlotRef = useRef<WebQueueSlot | null>(null);
    const naturalAdvanceInFlightRef = useRef(false);
    const naturalAdvanceSourceUriRef = useRef<string | null>(null);
    const naturalAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const airPlayAudioSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const airPlayAudioSourceReplacementRef =
      useRef<AirPlayAudioSourceReplacement | null>(null);
    const advanceNaturalQueueBoundaryRef = useRef<() => Promise<void>>(
      async () => {}
    );
    const [thumbnailSprites, setThumbnailSprites] = useState<ThumbnailImage[] | undefined>();
    const [startedSourceUri, setStartedSourceUri] = useState<string | null>(null);
    const [sourceRevealSuppressed, setSourceRevealSuppressed] = useState(false);
    // Preserve user intent across HLS/provider churn. Loading or promoting a
    // source can emit `pause` even though nobody pressed Pause; that transport
    // event must not feed back into the controlled `playing` prop.
    const sourceTransitionActiveRef = useRef(false);
    // Video.js derives controlsVisible from `userActive || paused`. Keep the
    // last settled pre-transition value because `ended` makes `paused` true
    // before React can render the successor.
    const stableControlsVisibleRef = useRef(true);
    const availableAudioTracksRef = useRef<VideoSurfaceAudioTrack[]>([]);
    const selectedAudioTrackRef = useRef<string | null>(null);
    const reportedVideoRenditionRef = useRef<{
      danceId: string | null;
      rendition: PlaybackVideoRendition | null;
    } | null>(null);
    const preferredAudioTrackRef = useRef<string | null>(preferredAudioTrack);
    const propagatedAudioTrackRef = useRef<string | null>(null);
    const selectAudioTrackRef = useRef<((id: string) => void) | null>(null);
    const fadeInActiveSlotRef = useRef<(slotIndex: 0 | 1) => void>(() => {});
    const silenceActiveSlotRef = useRef<(slotIndex: 0 | 1) => void>(() => {});
    const repreparePreloadedBandwidthSlotRef = useRef<
      (slotIndex: 0 | 1, measuredBandwidthBps: number) => void
    >(() => {});
    preferredAudioTrackRef.current = preferredAudioTrack;

    useEffect(
      () => () => {
        if (seekReleaseTimerRef.current) clearTimeout(seekReleaseTimerRef.current);
        slots.forEach((slot) => {
          slot.audioSourceNode?.disconnect();
          slot.transportGainNode?.disconnect();
        });
        const context = audioContextRef.current;
        audioContextRef.current = null;
        if (context && context.state !== "closed") void context.close();
      },
      [slots]
    );

    // Event handlers read the latest callbacks without re-binding listeners.
    const callbacksRef = useRef({
      onTime,
      onSeekStart,
      onEnded,
      onError,
      onFirstFrame,
      onQueuedItemAdopted,
      onExternalPlaybackChange,
      onPlayingChange,
      onMutedChange,
      onPlaybackRateChange,
      onAvailableAudioTracksChange,
      onSelectedAudioTrackChange,
      onVideoRenditionChange,
      onRemotePlaybackAvailabilityChange
    });
    useLayoutEffect(() => {
      callbacksRef.current = {
        onTime,
        onSeekStart,
        onEnded,
        onError,
        onFirstFrame,
        onQueuedItemAdopted,
        onExternalPlaybackChange,
        onPlayingChange,
        onMutedChange,
        onPlaybackRateChange,
        onAvailableAudioTracksChange,
        onSelectedAudioTrackChange,
        onVideoRenditionChange,
        onRemotePlaybackAvailabilityChange
      };
    });

    const clearAirPlayNaturalAdvanceTimer = useCallback(() => {
      if (naturalAdvanceTimerRef.current === null) return;
      clearTimeout(naturalAdvanceTimerRef.current);
      naturalAdvanceTimerRef.current = null;
    }, []);

    const scheduleAirPlayNaturalAdvance = useCallback(
      (element: HTMLVideoElement) => {
        clearAirPlayNaturalAdvanceTimer();
        const currentSlotIndex = activeSlotRef.current;
        const slot = slots[currentSlotIndex];
        const standbySlotIndex = (currentSlotIndex === 0 ? 1 : 0) as 0 | 1;
        const expectedUri = nextSourceUriRef.current;
        const nextPrepared = Boolean(
          expectedUri && slots[standbySlotIndex].uri === expectedUri
        );
        const delayMs = webKitAirPlayNaturalAdvanceDelayMs({
          alreadyAdvancing: naturalAdvanceInFlightRef.current,
          currentTime: element.currentTime,
          duration: element.duration,
          nextPrepared,
          paused: element.paused,
          playbackRate: element.playbackRate,
          playingIntent: playingIntentRef.current,
          wireless: isWebKitWirelessPlaybackActive(element)
        });
        if (
          delayMs === null ||
          slot.element !== element ||
          !slot.uri ||
          !expectedUri
        ) {
          return;
        }

        const scheduledSourceUri = slot.uri;
        naturalAdvanceTimerRef.current = setTimeout(() => {
          naturalAdvanceTimerRef.current = null;
          if (
            activeSlotRef.current !== currentSlotIndex ||
            slot.element !== element ||
            slot.uri !== scheduledSourceUri ||
            nextSourceUriRef.current !== expectedUri ||
            slots[standbySlotIndex].uri !== expectedUri ||
            element.paused ||
            element.seeking ||
            !playingIntentRef.current ||
            !isWebKitWirelessPlaybackActive(element)
          ) {
            return;
          }
          logWebQueue("airplay-natural-boundary", {
            remainingMs: Math.round(
              Math.max(0, element.duration - element.currentTime) * 1_000
            ),
            slot: currentSlotIndex
          });
          void advanceNaturalQueueBoundaryRef.current();
        }, delayMs);
      },
      [clearAirPlayNaturalAdvanceTimer, slots]
    );

    useEffect(
      () => () => clearAirPlayNaturalAdvanceTimer(),
      [clearAirPlayNaturalAdvanceTimer]
    );

    const publishRemotePlaybackState = useCallback(
      (
        element: HTMLVideoElement | null,
        availability = webKitPlaybackTargetAvailability(element)
      ) => {
        callbacksRef.current.onExternalPlaybackChange?.(
          isWebKitWirelessPlaybackActive(element)
        );
        callbacksRef.current.onRemotePlaybackAvailabilityChange?.(availability);
      },
      []
    );

    const releasePreferredAudioGuard = useCallback(
      (slot: WebQueueSlot) => {
        slot.requiresPreferredAudioBeforeAudible = false;
        const slotIndex = slots.indexOf(slot);
        if (slotIndex !== activeSlotRef.current || !slot.element) return;
        if (mutedIntentRef.current) slot.audioGuardMuted = false;
        const canFadeLocally = !hasExternalPlaybackRoute(slot.element);
        if (canFadeLocally && !mutedIntentRef.current && playingIntentRef.current) {
          silenceActiveSlotRef.current(slotIndex as 0 | 1);
        }
        slot.element.muted = mutedIntentRef.current;
        if (
          canFadeLocally &&
          !mutedIntentRef.current &&
          playingIntentRef.current
        ) {
          fadeInActiveSlotRef.current(slotIndex as 0 | 1);
        }
      },
      [slots]
    );

    const markPreferredAudioTrackCommitted = useCallback(
      (slot: WebQueueSlot, logicalId: string) => {
        if (logicalId !== preferredAudioTrackRef.current) return;
        slot.preferredAudioTrackCommitted = logicalId;
        releasePreferredAudioGuard(slot);
        const slotIndex = slots.indexOf(slot);
        logWebQueue("audio-track-committed", {
          active: slotIndex === activeSlotRef.current,
          logicalId,
          slot: slotIndex
        });
      },
      [releasePreferredAudioGuard, slots]
    );

    const audioTracksForSlot = useCallback((slot: WebQueueSlot) => {
      return webAudioTracksForMedia(
        slot.media.audioTracks,
        slot.element,
        slot.nativeHlsSourceOwned
      );
    }, []);

    const applyPreferredAudioTrackToSlot = useCallback(
      (slot: WebQueueSlot) => {
        const preferredId = preferredAudioTrackRef.current;
        const preference = hlsAudioPreferenceForId(
          slot.audioTrackDescriptors,
          preferredId
        );
        if (!preferredId || !preference) {
          releasePreferredAudioGuard(slot);
          return;
        }

        const wireless = isWebKitWirelessPlaybackActive(slot.element);
        const nativeHlsOwnsAudio = wireless || slot.nativeHlsSourceOwned;
        const rawTracks = nativeHlsOwnsAudio
          ? webAudioTracksForMedia(undefined, slot.element, true)
          : audioTracksForSlot(slot);
        const mediaTrackArray = webAudioTrackArray(rawTracks);
        if (
          nativeHlsOwnsAudio &&
          mediaTrackArray.length < slot.audioTrackDescriptors.length
        ) {
          return;
        }
        const mediaTracks = normalizedWebAudioTracks(mediaTrackArray);
        const preferredIndex = mediaAudioTrackIndexForLogicalId(
          slot.audioTrackDescriptors,
          mediaTracks,
          preferredId
        );
        // hls.js stays allocated after Safari hands the element to AirPlay,
        // but its local AudioTrackList no longer owns the audible pipeline.
        const engine = nativeHlsOwnsAudio ? null : slot.media.engine;
        if (engine) {
          if (
            preferredIndex >= 0 &&
            engine.audioTrack === preferredIndex &&
            slot.preferredAudioTrackCommitted === preferredId
          ) {
            releasePreferredAudioGuard(slot);
          } else {
            engine.setAudioOption(preference);
          }
        }
        if (preferredIndex < 0) return;
        const preferredTrack = mediaTracks[preferredIndex];
        if (rawTracks) {
          selectWebAudioTrack(rawTracks, preferredIndex);
        }
        if (!engine && preferredTrack) {
          markPreferredAudioTrackCommitted(slot, preferredId);
        }
      },
      [
        audioTracksForSlot,
        markPreferredAudioTrackCommitted,
        releasePreferredAudioGuard
      ]
    );

    const syncAirPlayAudioPreference = useCallback(
      (slot: WebQueueSlot, reason: string, report = true) => {
        if (!slot.element || !isWebKitWirelessPlaybackActive(slot.element)) {
          return false;
        }
        const receiverTracks = webAudioTrackArray(
          webKitWirelessAudioTracks(slot.element)
        );
        if (report) {
          logWebQueue("airplay-audio-preference-sync", {
            descriptorCount: slot.audioTrackDescriptors.length,
            preferredId: preferredAudioTrackRef.current,
            reason,
            slot: slots.indexOf(slot),
            trackCount: receiverTracks.length
          });
        }
        if (receiverTracks.length < slot.audioTrackDescriptors.length) {
          return false;
        }
        applyPreferredAudioTrackToSlot(slot);
        return true;
      },
      [applyPreferredAudioTrackToSlot, slots]
    );

    const clearAirPlayAudioPreferenceSync = useCallback(() => {
      if (airPlayAudioSyncTimerRef.current === null) return;
      clearTimeout(airPlayAudioSyncTimerRef.current);
      airPlayAudioSyncTimerRef.current = null;
    }, []);

    const scheduleAirPlayAudioPreferenceSync = useCallback(
      (slot: WebQueueSlot, reason: string) => {
        clearAirPlayAudioPreferenceSync();
        const element = slot.element;
        const generation = slot.generation;
        let attempt = 0;
        const sync = () => {
          airPlayAudioSyncTimerRef.current = null;
          if (
            slot.element !== element ||
            slot.generation !== generation ||
            slots.indexOf(slot) !== activeSlotRef.current ||
            !isWebKitWirelessPlaybackActive(element)
          ) {
            return;
          }
          const complete = syncAirPlayAudioPreference(
            slot,
            reason,
            attempt === 0
          );
          if (complete) {
            if (attempt > 0) {
              logWebQueue("airplay-audio-preference-ready", {
                attempts: attempt,
                preferredId: preferredAudioTrackRef.current,
                reason,
                slot: slots.indexOf(slot)
              });
            }
            return;
          }
          const trackCount = webAudioTrackArray(
            webKitWirelessAudioTracks(element)
          ).length;
          const delayMs = webKitAirPlayAudioTrackRetryDelayMs({
            attempt,
            descriptorCount: slot.audioTrackDescriptors.length,
            trackCount,
            wireless: isWebKitWirelessPlaybackActive(element)
          });
          if (delayMs === null) {
            logWebQueue("airplay-audio-preference-incomplete", {
              attempts: attempt,
              descriptorCount: slot.audioTrackDescriptors.length,
              preferredId: preferredAudioTrackRef.current,
              reason,
              slot: slots.indexOf(slot),
              trackCount
            });
            return;
          }
          attempt += 1;
          airPlayAudioSyncTimerRef.current = setTimeout(sync, delayMs);
        };
        sync();
      }, [
        clearAirPlayAudioPreferenceSync,
        slots,
        syncAirPlayAudioPreference
      ]
    );

    useEffect(
      () => () => clearAirPlayAudioPreferenceSync(),
      [clearAirPlayAudioPreferenceSync]
    );

    const prepareAirPlayAudioSource = useCallback(
      (slot: WebQueueSlot, reason: string) => {
        const preferredId = preferredAudioTrackRef.current;
        const prepared = Boolean(
          slot.uri &&
          slot.element &&
          !isWebKitWirelessPlaybackActive(slot.element) &&
          primeWebKitAirPlaySource(
            slot.element,
            webHlsUriWithAudioPreference(
              slot.uri,
              slot.audioTrackDescriptors,
              preferredId
            )
          )
        );
        logWebQueue("airplay-audio-source-prepared", {
          active: slots.indexOf(slot) === activeSlotRef.current,
          preferredId,
          prepared,
          reason,
          slot: slots.indexOf(slot),
          wireless: isWebKitWirelessPlaybackActive(slot.element)
        });
        return prepared;
      },
      [slots]
    );

    const publishAudioTrackState = useCallback(
      (
        tracks: VideoSurfaceAudioTrack[],
        selectedMediaId: string | null,
        selectMediaTrack: ((id: string) => void) | null
      ) => {
        const descriptors = slots[activeSlotRef.current].audioTrackDescriptors;
        const activeTracks = audioTracksForSlot(slots[activeSlotRef.current]);
        const activeTrackArray = webAudioTrackArray(activeTracks);
        const nativeTracks = normalizedWebAudioTracks(activeTrackArray);
        const effectiveTracks = tracks.length > 0 ? tracks : nativeTracks;
        const selectedNativeIndex = nativeTracks.findIndex(
          (track) => track.enabled
        );
        const effectiveSelectedMediaId =
          tracks.length > 0
            ? selectedMediaId
            : nativeTracks[selectedNativeIndex]?.id ?? null;
        const effectiveSelectMediaTrack =
          tracks.length > 0
            ? selectMediaTrack
            : activeTracks
              ? (mediaId: string) => {
                  const selectedIndex = nativeTracks.findIndex(
                    (track) => track.id === mediaId
                  );
                  if (selectedIndex < 0) return;
                  selectWebAudioTrack(activeTracks, selectedIndex);
                }
              : null;
        const { available, selectedId } = mapWebAudioTrackState(
          effectiveTracks,
          effectiveSelectedMediaId,
          descriptors
        );
        const select = effectiveSelectMediaTrack
          ? (logicalId: string) => {
              const index = mediaAudioTrackIndexForLogicalId(
                descriptors,
                effectiveTracks,
                logicalId
              );
              const mediaId = effectiveTracks[index]?.id;
              if (mediaId) effectiveSelectMediaTrack(mediaId);
            }
          : null;
        const preferredId = preferredAudioTrackRef.current;
        const preferredAvailable = Boolean(
          preferredId &&
            available.some((track) => track.id === preferredId)
        );
        // The app's feed choice is an intent boundary. Safari can keep
        // reporting the outgoing AirPlay rendition until its receiver finishes
        // negotiating; publishing that stale value would undo the choice and
        // prepare the successor with the wrong feed.
        const reportedSelectedId = reportedWebAudioTrackId(
          available,
          selectedId,
          preferredId
        );
        availableAudioTracksRef.current = available;
        selectedAudioTrackRef.current = reportedSelectedId;
        selectAudioTrackRef.current = select;
        callbacksRef.current.onAvailableAudioTracksChange?.(available);
        callbacksRef.current.onSelectedAudioTrackChange?.(reportedSelectedId);
        if (
          preferredId &&
          preferredAvailable &&
          preferredId !== selectedId &&
          select
        ) {
          select(preferredId);
        }
      },
      [audioTracksForSlot, slots]
    );

    const publishActiveVideoRendition = useCallback(
      (slot: WebQueueSlot) => {
        if (slots.indexOf(slot) !== activeSlotRef.current) return;
        const previous = reportedVideoRenditionRef.current;
        if (
          previous?.danceId === slot.danceId &&
          sameVideoRendition(previous.rendition, slot.videoRendition)
        ) {
          return;
        }
        reportedVideoRenditionRef.current = {
          danceId: slot.danceId,
          rendition: slot.videoRendition
        };
        callbacksRef.current.onVideoRenditionChange?.(
          slot.danceId,
          slot.videoRendition
        );
      },
      [slots]
    );

    const updateRenderedVideoRendition = useCallback(
      (slot: WebQueueSlot, levelIndex = slot.media.engine?.currentLevel ?? -1) => {
        const level = levelIndex >= 0
          ? slot.media.engine?.levels[levelIndex]
          : undefined;
        const width = slot.element?.videoWidth || level?.width || 0;
        const height = slot.element?.videoHeight || level?.height || 0;
        slot.videoRendition = width > 0 && height > 0
          ? {
              width,
              height,
              codec: playbackVideoCodec(level?.videoCodec)
            }
          : null;
        publishActiveVideoRendition(slot);
      },
      [publishActiveVideoRendition]
    );

    const bindSlotAudioTracks = useCallback(
      (slot: WebQueueSlot) => {
        slot.trackBinding?.abort();
        slot.unbindEngineAudioTracks?.();
        slot.unbindEngineVideoRendition?.();
        slot.unbindEngineAudioTracks = null;
        slot.unbindEngineVideoRendition = null;
        slot.trackBinding = new AbortController();
        const signal = slot.trackBinding.signal;
        const bind = () => {
          const tracks = audioTracksForSlot(slot);
          if (signal.aborted) return;
          if (tracks) {
            const syncTracks = () => {
              applyPreferredAudioTrackToSlot(slot);
              if (slots.indexOf(slot) !== activeSlotRef.current) return;
              publishAudioTrackState([], null, null);
            };
            tracks.addEventListener("addtrack", syncTracks, { signal });
            tracks.addEventListener("removetrack", syncTracks, { signal });
            tracks.addEventListener("change", syncTracks, { signal });
          }
          const engine = slot.media.engine;
          const syncRenderedVideoRendition = () => updateRenderedVideoRendition(slot);
          slot.element?.addEventListener("loadeddata", syncRenderedVideoRendition, {
            signal
          });
          slot.element?.addEventListener("resize", syncRenderedVideoRendition, {
            signal
          });
          if (!engine) {
            applyPreferredAudioTrackToSlot(slot);
            syncRenderedVideoRendition();
            if (slots.indexOf(slot) === activeSlotRef.current) {
              publishAudioTrackState([], null, null);
            }
            return;
          }
          // Video.js mirrors hls.js tracks after AUDIO_TRACKS_UPDATED, but the
          // engine's switched event is the authoritative point at which an
          // already-buffered standby slot is safe to make audible.
          const handleAudioTracksUpdated = () => applyPreferredAudioTrackToSlot(slot);
          const handleAudioTrackSwitching = (
            _event: Events.AUDIO_TRACK_SWITCHING,
            data: { id: number }
          ) => {
            const tracks = audioTracksForSlot(slot);
            const mediaTracks = normalizedWebAudioTracks(
              webAudioTrackArray(tracks)
            );
            const logicalId = logicalAudioTrackIdForMediaIndex(
              slot.audioTrackDescriptors,
              mediaTracks,
              data.id
            );
            if (
              logicalId === preferredAudioTrackRef.current &&
              slot.requiresPreferredAudioBeforeAudible &&
              slots.indexOf(slot) === activeSlotRef.current &&
              slot.element
            ) {
              slot.audioGuardMuted = true;
              slot.element.muted = true;
            }
          };
          const handleAudioTrackSwitched = (
            _event: Events.AUDIO_TRACK_SWITCHED,
            data: { id: number }
          ) => {
            const tracks = audioTracksForSlot(slot);
            const mediaTracks = normalizedWebAudioTracks(
              webAudioTrackArray(tracks)
            );
            const logicalId = logicalAudioTrackIdForMediaIndex(
              slot.audioTrackDescriptors,
              mediaTracks,
              data.id
            );
            if (logicalId) markPreferredAudioTrackCommitted(slot, logicalId);
          };
          engine.on(Events.AUDIO_TRACKS_UPDATED, handleAudioTracksUpdated);
          engine.on(Events.AUDIO_TRACK_SWITCHING, handleAudioTrackSwitching);
          engine.on(Events.AUDIO_TRACK_SWITCHED, handleAudioTrackSwitched);
          const handleFragmentBuffered = (
            _event: Events.FRAG_BUFFERED,
            data: { frag: { sn: number | "initSegment"; type: string } }
          ) => {
            // Init-map fetches occur before hls.js has a real media-throughput
            // sample. Treating that 10 Mb/s seed as a measurement permanently
            // stranded the successor on its cold-start 1080p choice.
            if (
              data.frag.type !== "main" ||
              data.frag.sn === "initSegment"
            ) {
              return;
            }
            if (slots.indexOf(slot) !== activeSlotRef.current) return;
            const measured = sharedWebQueueBandwidthEstimate(
              engine.bandwidthEstimate
            );
            if (measured === null) return;
            sessionBandwidthEstimateRef.current = measured;
            sessionHasMeasuredBandwidthRef.current = true;

            const standby = slots[activeSlotRef.current === 0 ? 1 : 0];
            if (
              !standby.uri ||
              standby.bandwidthEstimateInherited
            ) {
              return;
            }
            standby.bandwidthEstimateInherited = true;
            standby.bandwidthEstimateSeeded = measured;
            logWebQueue("bandwidth-handoff", {
              estimateMbps: Math.round(measured / 100_000) / 10,
              slot: slots.indexOf(standby)
            });
            // The hidden player may have already buffered its entire 1080p
            // warm window while both cold engines shared the 10 Mb/s seed.
            // Rebuild only that invisible successor once, using the real
            // measurement before manifest selection, so its first displayed
            // frame can be 4K rather than waiting for stale 1080p buffer to
            // drain after promotion.
            repreparePreloadedBandwidthSlotRef.current(
              slots.indexOf(standby) as 0 | 1,
              measured
            );
          };
          // FRAG_LOADED precedes hls.js's ABR sample update. FRAG_BUFFERED is
          // the first event where `bandwidthEstimate` contains this media
          // fragment's measured throughput rather than the cold-start seed.
          engine.on(Events.FRAG_BUFFERED, handleFragmentBuffered);
          const handleLevelSwitched = (
            _event: Events.LEVEL_SWITCHED,
            data: { level: number }
          ) => updateRenderedVideoRendition(slot, data.level);
          engine.on(Events.LEVEL_SWITCHED, handleLevelSwitched);
          slot.unbindEngineAudioTracks = () => {
            engine.off(Events.AUDIO_TRACKS_UPDATED, handleAudioTracksUpdated);
            engine.off(Events.AUDIO_TRACK_SWITCHING, handleAudioTrackSwitching);
            engine.off(Events.AUDIO_TRACK_SWITCHED, handleAudioTrackSwitched);
            engine.off(Events.FRAG_BUFFERED, handleFragmentBuffered);
          };
          slot.unbindEngineVideoRendition = () => {
            engine.off(Events.LEVEL_SWITCHED, handleLevelSwitched);
          };
          applyPreferredAudioTrackToSlot(slot);
          syncRenderedVideoRendition();
        };
        queueMicrotask(bind);
      },
      [
        applyPreferredAudioTrackToSlot,
        audioTracksForSlot,
        markPreferredAudioTrackCommitted,
        publishAudioTrackState,
        releasePreferredAudioGuard,
        slots,
        updateRenderedVideoRendition
      ]
    );

    useWebQueueSlotLifecycle(slots[0], bindSlotAudioTracks);
    useWebQueueSlotLifecycle(slots[1], bindSlotAudioTracks);

    const exitOwnedPictureInPicture = useCallback(
      (reason: string, candidates: Array<HTMLVideoElement | null> = []) => {
        if (typeof document === "undefined") return;
        const activeElement = document.pictureInPictureElement;
        const ownedElements = [
          pictureInPictureElementRef.current,
          ...slots.map((slot) => slot.element),
          ...candidates
        ];
        if (!activeElement || !ownedElements.includes(activeElement as HTMLVideoElement)) return;

        logWebQueue("pip-exit", { reason });
        void document.exitPictureInPicture().catch((error: unknown) => {
          logWebQueue("pip-exit-rejected", {
            error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
            reason
          });
        });
      },
      [slots]
    );

    const transferPictureInPicture = useCallback(
      async (outgoing: HTMLVideoElement, incoming: HTMLVideoElement) => {
        if (
          typeof document === "undefined" ||
          document.pictureInPictureElement !== outgoing
        ) {
          return;
        }

        pictureInPictureElementRef.current = outgoing;
        logWebQueue("pip-transfer-armed", {
          incomingReadyState: incoming.readyState
        });
        try {
          await waitForPictureInPictureReady(incoming);
          if (
            document.pictureInPictureElement !== outgoing ||
            videoRef.current !== incoming
          ) {
            return;
          }
          const requestPictureInPicture = incoming.requestPictureInPicture;
          if (typeof requestPictureInPicture !== "function") {
            throw new Error("Picture-in-Picture transfer is unsupported.");
          }
          logWebQueue("pip-transfer-request", {
            incomingReadyState: incoming.readyState
          });
          await requestPictureInPicture.call(incoming);
          if (document.pictureInPictureElement !== incoming) {
            throw new Error("Incoming video did not take ownership of Picture-in-Picture.");
          }
          pictureInPictureElementRef.current = incoming;
          logWebQueue("pip-transfer-complete", {});
        } catch (error) {
          const activeElement = document.pictureInPictureElement;
          if (activeElement !== outgoing && activeElement !== incoming) return;
          logWebQueue("pip-transfer-rejected", {
            error: error instanceof Error ? `${error.name}: ${error.message}` : String(error)
          });
          exitOwnedPictureInPicture("transfer-rejected", [outgoing, incoming]);
        }
      },
      [exitOwnedPictureInPicture]
    );

    useLayoutEffect(() => {
      if (!sourceUri) exitOwnedPictureInPicture("source-cleared");
    }, [exitOwnedPictureInPicture, sourceUri]);

    useLayoutEffect(
      () => () => exitOwnedPictureInPicture("surface-unmounted"),
      [exitOwnedPictureInPicture]
    );

    const beginActiveSourceTransition = useCallback(() => {
      sourceTransitionActiveRef.current = playingIntentRef.current;
      // The outgoing item may still satisfy sourcePlaybackStarted during the
      // pre-ended pause. Clear it synchronously so the continuity bridge cannot
      // mistake the old item for the newly started successor and release the
      // hidden-controls guard before promotion.
      setStartedSourceUri(null);
      logWebQueue("transition-begin", {
        activeSlot: activeSlotRef.current,
        controlsVisible: stableControlsVisibleRef.current,
        playingIntent: playingIntentRef.current
      });
      if (!stableControlsVisibleRef.current && !controlsPinned) {
        setSourceRevealSuppressed(true);
      }
    }, [controlsPinned]);

    const ensureTransportGraph = useCallback((slot: WebQueueSlot) => {
      const element = slot.element;
      if (
        !element ||
        !supportsWebAudioTransportEnvelope() ||
        hasExternalPlaybackRoute(element)
      ) {
        return null;
      }

      let context = audioContextRef.current;
      if (!context || context.state === "closed") {
        // Never reroute an already-audible element into a newly suspended
        // context during autonomous playback. The first graph is created only
        // inside a real transport gesture; once authorized, later queue
        // promotions can safely attach their persistent standby slot.
        if (navigator.userActivation?.isActive !== true) return null;
        context = new AudioContext({ latencyHint: "interactive" });
        audioContextRef.current = context;
        logWebQueue("transport-audio-context", { state: context.state });
      }
      if (!slot.audioSourceNode || !slot.transportGainNode) {
        const source = context.createMediaElementSource(element);
        const gain = context.createGain();
        gain.gain.value = 1;
        source.connect(gain).connect(context.destination);
        slot.audioSourceNode = source;
        slot.transportGainNode = gain;
        logWebQueue("transport-audio-slot", {
          slot: slots.indexOf(slot),
          state: context.state
        });
      }
      return { context, gain: slot.transportGainNode };
    }, [slots]);

    const activateTransportGraph = useCallback(
      async (slot: WebQueueSlot) => {
        const graph = ensureTransportGraph(slot);
        if (!graph) return null;
        if (graph.context.state === "suspended") {
          try {
            await graph.context.resume();
          } catch {
            return null;
          }
        }
        return graph.context.state === "running" ? graph : null;
      },
      [ensureTransportGraph]
    );

    const cancelTransportGain = useCallback((slot: WebQueueSlot) => {
      slot.transportGainGeneration += 1;
      const context = audioContextRef.current;
      const gain = slot.transportGainNode?.gain;
      if (context && gain) {
        const now = context.currentTime;
        if (typeof gain.cancelAndHoldAtTime === "function") {
          gain.cancelAndHoldAtTime(now);
        } else {
          const heldValue = gain.value;
          gain.cancelScheduledValues(now);
          gain.setValueAtTime(heldValue, now);
        }
      }
    }, []);

    const setTransportGainImmediately = useCallback(
      async (slot: WebQueueSlot, value: number) => {
        const graph = await activateTransportGraph(slot);
        if (!graph) return false;
        cancelTransportGain(slot);
        const now = graph.context.currentTime;
        graph.gain.gain.cancelScheduledValues(now);
        graph.gain.gain.setValueAtTime(Math.min(1, Math.max(0, value)), now);
        return true;
      },
      [activateTransportGraph, cancelTransportGain]
    );

    const silenceActiveSlot = useCallback(
      (slotIndex: 0 | 1) => {
        const slot = slots[slotIndex];
        const context = audioContextRef.current;
        const gain = slot.transportGainNode?.gain;
        if (!context || !gain) return;
        cancelTransportGain(slot);
        gain.setValueAtTime(0, context.currentTime);
      },
      [cancelTransportGain, slots]
    );
    silenceActiveSlotRef.current = silenceActiveSlot;

    const rampTransportGain = useCallback(
      async (slot: WebQueueSlot, targetGain: number) => {
        const element = slot.element;
        if (!element || element.muted || hasExternalPlaybackRoute(element)) {
          return false;
        }
        const graph = await activateTransportGraph(slot);
        if (!graph || slot.element !== element) return false;

        cancelTransportGain(slot);
        const generation = slot.transportGainGeneration;
        const now = graph.context.currentTime;
        const gain = graph.gain.gain;
        if (typeof gain.cancelAndHoldAtTime === "function") {
          gain.cancelAndHoldAtTime(now);
        } else {
          const heldValue = gain.value;
          gain.cancelScheduledValues(now);
          gain.setValueAtTime(heldValue, now);
        }
        gain.linearRampToValueAtTime(
          Math.min(1, Math.max(0, targetGain)),
          now + TRANSPORT_DECLICK_MS / 1_000
        );

        await new Promise((resolve) =>
          setTimeout(resolve, TRANSPORT_DECLICK_MS + 4)
        );
        const completed =
          slot.transportGainGeneration === generation &&
          slot.element === element &&
          !hasExternalPlaybackRoute(element);
        return completed;
      },
      [activateTransportGraph, cancelTransportGain]
    );

    useEffect(() => {
      let animationFrame = 0;
      const watchNaturalEnd = () => {
        const slot = slots[activeSlotRef.current];
        const element = slot.element;
        const remainingSeconds =
          element && Number.isFinite(element.duration)
            ? element.duration - element.currentTime
            : Number.POSITIVE_INFINITY;
        const insideEnvelopeWindow = Boolean(
          element &&
            shouldBeginNaturalEndEnvelope({
              alreadyArmed: naturalEndEnvelopeSlotRef.current === slot,
              currentTime: element.currentTime,
              duration: element.duration,
              externalPlayback: hasExternalPlaybackRoute(element),
              muted: element.muted,
              paused: element.paused,
              playingIntent: playingIntentRef.current
            })
        );

        if (
          insideEnvelopeWindow &&
          naturalEndEnvelopeSlotRef.current !== slot
        ) {
          naturalEndEnvelopeSlotRef.current = slot;
          logWebQueue("natural-end-envelope", {
            remainingMs: Math.round(remainingSeconds * 1_000),
            slot: activeSlotRef.current
          });
          void rampTransportGain(slot, 0);
        } else if (
          naturalEndEnvelopeSlotRef.current === slot &&
          remainingSeconds > 0.25
        ) {
          // A seek away from the tail re-arms the envelope. The normal seek
          // release owns the audible restoration.
          naturalEndEnvelopeSlotRef.current = null;
        }
        animationFrame = requestAnimationFrame(watchNaturalEnd);
      };
      animationFrame = requestAnimationFrame(watchNaturalEnd);
      return () => cancelAnimationFrame(animationFrame);
    }, [rampTransportGain, slots]);

    const restoreTransportGain = useCallback((slot: WebQueueSlot) => {
      const context = audioContextRef.current;
      const gain = slot.transportGainNode?.gain;
      if (!context || !gain) return;
      cancelTransportGain(slot);
      gain.setValueAtTime(1, context.currentTime);
    }, [cancelTransportGain]);

    const fadeInActiveSlot = useCallback(
      (slotIndex: 0 | 1) => {
        const slot = slots[slotIndex];
        const element = slot.element;
        if (!element || hasExternalPlaybackRoute(element)) return;
        if (element.muted || slot.requiresPreferredAudioBeforeAudible) {
          restoreTransportGain(slot);
          return;
        }
        void rampTransportGain(slot, 1);
      },
      [rampTransportGain, restoreTransportGain, slots]
    );
    fadeInActiveSlotRef.current = fadeInActiveSlot;

    const beginSeekWithDeClick = useCallback((): Promise<void> => {
      if (seekFadePromiseRef.current) return seekFadePromiseRef.current;
      if (seekEnvelopePendingRef.current && seekEnvelopeSilencedRef.current) {
        return Promise.resolve();
      }
      const slotIndex = activeSlotRef.current;
      const slot = slots[slotIndex];
      const element = slot.element;
      seekEnvelopePendingRef.current = true;
      if (!element || element.paused || element.muted) {
        seekEnvelopeSilencedRef.current = true;
        return Promise.resolve();
      }
      const fade = rampTransportGain(slot, 0).then((completed) => {
        seekEnvelopeSilencedRef.current = completed;
      }).finally(() => {
        if (seekFadePromiseRef.current === fade) seekFadePromiseRef.current = null;
      });
      seekFadePromiseRef.current = fade;
      return fade;
    }, [rampTransportGain, slots]);

    const finishSeekWithDeClick = useCallback(() => {
      if (!seekEnvelopePendingRef.current) return;
      seekEnvelopePendingRef.current = false;
      seekEnvelopeSilencedRef.current = false;
      seekFadePromiseRef.current = null;
      if (seekReleaseTimerRef.current) {
        clearTimeout(seekReleaseTimerRef.current);
        seekReleaseTimerRef.current = null;
      }
      const slotIndex = activeSlotRef.current;
      const element = slots[slotIndex].element;
      if (element && !element.paused && playingIntentRef.current) {
        fadeInActiveSlot(slotIndex);
      } else {
        restoreTransportGain(slots[slotIndex]);
      }
    }, [fadeInActiveSlot, restoreTransportGain, slots]);

    const seekWithDeClick = useCallback(
      async (seconds: number) => {
        const slotIndex = activeSlotRef.current;
        const slot = slots[slotIndex];
        const video = slot.element;
        if (!video || !Number.isFinite(seconds)) return;
        if (!video.paused && !video.muted) await beginSeekWithDeClick();
        const total = video.duration;
        if (Number.isFinite(total)) {
          video.currentTime = Math.min(Math.max(0, seconds), Math.max(0, total - 0.25));
          seekReleaseTimerRef.current = setTimeout(
            finishSeekWithDeClick,
            250
          );
        } else {
          pendingSeekRef.current = seconds;
          finishSeekWithDeClick();
        }
      },
      [beginSeekWithDeClick, finishSeekWithDeClick, slots]
    );

    useLayoutEffect(() => {
      const restorers = slots.map((slot, rawSlotIndex) => {
        const slotIndex = rawSlotIndex as 0 | 1;
        const previousDescriptor = Object.getOwnPropertyDescriptor(
          slot.media,
          "currentTime"
        );
        Object.defineProperty(slot.media, "currentTime", {
          configurable: true,
          get: () => slot.element?.currentTime ?? 0,
          set: (seconds: number) => {
            if (slotIndex === activeSlotRef.current) {
              void seekWithDeClick(seconds);
            } else if (slot.element && Number.isFinite(seconds)) {
              slot.element.currentTime = seconds;
            }
          }
        });
        return () => {
          if (previousDescriptor) {
            Object.defineProperty(slot.media, "currentTime", previousDescriptor);
          } else {
            Reflect.deleteProperty(slot.media, "currentTime");
          }
        };
      });
      return () => restorers.forEach((restore) => restore());
    }, [seekWithDeClick, slots]);

    const pauseWithDeClick = useCallback(
      async (slotIndex: 0 | 1, force = false) => {
        const slot = slots[slotIndex];
        const element = slot.element;
        if (!element || element.paused) return;
        if (
          element.muted ||
          hasExternalPlaybackRoute(element)
        ) {
          element.pause();
          return;
        }
        const completed = await rampTransportGain(slot, 0);
        if (!completed || slot.element !== element) {
          element.pause();
          return;
        }
        if (!force && playingIntentRef.current) {
          fadeInActiveSlot(slotIndex);
          return;
        }
        element.pause();
        // Restore the private graph while playback is silent. Video.js's
        // public volume remains untouched throughout the envelope.
        restoreTransportGain(slot);
      },
      [fadeInActiveSlot, rampTransportGain, restoreTransportGain, slots]
    );

    const playWithIntent = useCallback(async (slotIndex: 0 | 1, element: HTMLVideoElement) => {
      const slot = slots[slotIndex];
      logWebQueue("play-request", {
        activeSlot: activeSlotRef.current,
        paused: element.paused,
        playingIntent: playingIntentRef.current,
        readyState: element.readyState,
        slot: slotIndex,
        transitionActive: sourceTransitionActiveRef.current
      });
      if (
        element.paused &&
        !element.muted &&
        !slot.requiresPreferredAudioBeforeAudible &&
        !hasExternalPlaybackRoute(element)
      ) {
        await setTransportGainImmediately(slot, 0);
        if (slot.element !== element || !playingIntentRef.current) return;
      } else if (!element.paused) {
        fadeInActiveSlot(slotIndex);
        return;
      }
      const attempt = element.play();
      if (!attempt) {
        fadeInActiveSlot(slotIndex);
        return;
      }
      void attempt.then(
        () => fadeInActiveSlot(slotIndex),
        (error: unknown) => {
        restoreTransportGain(slot);
        logWebQueue("play-rejected", {
          activeSlot: activeSlotRef.current,
          error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
          playingIntent: playingIntentRef.current,
          slot: slotIndex
        });
        // A source replacement can race an imperative queue/resume request.
        // Do not rewrite app intent from that transport-level rejection;
        // actual media pause events remain the source of truth for a
        // user-driven pause.
      });
    }, [
      cancelTransportGain,
      fadeInActiveSlot,
      restoreTransportGain,
      setTransportGainImmediately,
      slots
    ]);

    const recoverBlockedNativeAutoplay = useCallback(
      (slotIndex: 0 | 1, element: HTMLVideoElement) => {
        const slot = slots[slotIndex];
        const sourceAtAttempt = slot.uri;
        if (
          !sourceAtAttempt ||
          slotIndex !== activeSlotRef.current ||
          nativeAutoplaySourceRef.current !== sourceAtAttempt ||
          !playingIntentRef.current ||
          !element.paused
        ) {
          return;
        }

        // Native autoplay has had its opportunity by `canplay`. If it is still
        // paused, retry audibly so an autoplay-authorized origin keeps sound.
        // Safari can reject that request on a reload without a user gesture.
        // Keep the user's audible intent, reveal the ready paused player, and
        // let the actual Play click provide the required gesture.
        nativeAutoplaySourceRef.current = null;
        logWebQueue("native-autoplay-retry", { slot: slotIndex });
        const audibleAttempt = element.play();
        if (!audibleAttempt) return;
        void audibleAttempt.catch((error: unknown) => {
          logWebQueue("native-autoplay-rejected", {
            error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
            slot: slotIndex
          });
          if (
            !isAudibleAutoplayPolicyRejection(error) ||
            slotIndex !== activeSlotRef.current ||
            slots[slotIndex].uri !== sourceAtAttempt ||
            !playingIntentRef.current
          ) {
            return;
          }

          logWebQueue("autoplay-blocked-paused", { slot: slotIndex });
          firstFrameFiredRef.current = true;
          callbacksRef.current.onFirstFrame?.();
          callbacksRef.current.onPlayingChange?.(false);
        });
      },
      [slots]
    );

    const configureSlot = useCallback(
      (
        slotIndex: 0 | 1,
        source: VideoSurfaceSource | null,
        generation: string | number | null
      ) => {
        const slot = slots[slotIndex];
        const uri = source?.uri ?? null;
        if (slot.uri === uri && slot.generation === generation) return;
        const shouldUseNativeAutoplay = Boolean(
          uri &&
            !slot.uri &&
            slotIndex === activeSlotRef.current &&
            slot.element?.autoplay &&
            playingIntentRef.current
        );
        logWebQueue("configure", {
          activeSlot: activeSlotRef.current,
          fromGeneration: webQueueGenerationLabel(slot.generation),
          fromUri: slot.uri ? "set" : null,
          playingIntent: playingIntentRef.current,
          slot: slotIndex,
          toGeneration: webQueueGenerationLabel(generation),
          toUri: uri ? "set" : null
        });
        if (slotIndex === activeSlotRef.current) beginActiveSourceTransition();
        if (slot.element && !slot.element.paused) slot.element.pause();
        slot.ready = false;
        slot.nativeHlsSourceOwned = false;
        slot.danceId = source?.danceId ?? null;
        slot.uri = uri;
        slot.generation = generation;
        slot.videoRendition = null;
        slot.bandwidthEstimateInherited =
          sessionHasMeasuredBandwidthRef.current;
        slot.bandwidthEstimateSeeded = sessionBandwidthEstimateRef.current;
        publishActiveVideoRendition(slot);
        slot.audioTrackDescriptors = source?.audioTracks ?? [];
        slot.preferredAudioTrackCommitted = null;
        slot.requiresPreferredAudioBeforeAudible = Boolean(
          hlsAudioPreferenceForId(
            slot.audioTrackDescriptors,
            preferredAudioTrackRef.current
          )
        );
        if (
          slot.requiresPreferredAudioBeforeAudible &&
          slotIndex === activeSlotRef.current &&
          slot.element
        ) {
          slot.audioGuardMuted = true;
          slot.element.muted = true;
        }
        if (!uri) {
          if (slotIndex === activeSlotRef.current) {
            nativeAutoplaySourceRef.current = null;
          }
          slot.media.engine?.stopLoad();
          return;
        }
        if (shouldUseNativeAutoplay) {
          nativeAutoplaySourceRef.current = uri;
          logWebQueue("native-autoplay-armed", { slot: slotIndex });
        }
        slot.media.config = webHlsMediaConfig(
          slot.audioTrackDescriptors,
          preferredAudioTrackRef.current,
          slot.bandwidthEstimateSeeded
        );
        slot.media.preload = "auto";
        setWebMediaSourceForPlaybackTarget(
          slot.media,
          slot.element,
          webHlsUriWithAudioPreference(
            uri,
            slot.audioTrackDescriptors,
            preferredAudioTrackRef.current
          )
        );
        bindSlotAudioTracks(slot);
      },
      [
        beginActiveSourceTransition,
        bindSlotAudioTracks,
        publishActiveVideoRendition,
        slots
      ]
    );

    const repreparePreloadedAudioSlot = useCallback(
      (slotIndex: 0 | 1, preferredId: string) => {
        const slot = slots[slotIndex];
        const uri = slot.uri;
        if (!uri) return;

        // This slot is hidden and paused, so rebuilding it has no visible
        // playback cost. Crucially, audioPreference is present before hls.js
        // parses the new manifest and buffers its first audio fragment.
        logWebQueue("audio-standby-reprepare", {
          logicalId: preferredId,
          slot: slotIndex
        });
        slot.element?.pause();
        slot.ready = false;
        slot.preferredAudioTrackCommitted = null;
        slot.requiresPreferredAudioBeforeAudible = true;
        slot.audioGuardMuted = true;
        if (slot.element) slot.element.muted = true;
        slot.trackBinding?.abort();
        slot.unbindEngineAudioTracks?.();
        slot.unbindEngineVideoRendition?.();
        slot.unbindEngineAudioTracks = null;
        slot.unbindEngineVideoRendition = null;
        slot.videoRendition = null;
        slot.bandwidthEstimateInherited =
          sessionHasMeasuredBandwidthRef.current;
        slot.bandwidthEstimateSeeded = sessionBandwidthEstimateRef.current;
        slot.media.config = webHlsMediaConfig(
          slot.audioTrackDescriptors,
          preferredId,
          slot.bandwidthEstimateSeeded
        );
        slot.media.preload = "auto";
        slot.media.src = webHlsUriWithAudioPreference(
          uri,
          slot.audioTrackDescriptors,
          preferredId
        );
        bindSlotAudioTracks(slot);
      },
      [bindSlotAudioTracks, slots]
    );

    const repreparePreloadedBandwidthSlot = useCallback(
      (slotIndex: 0 | 1, measuredBandwidthBps: number) => {
        const slot = slots[slotIndex];
        const uri = slot.uri;
        if (!uri) return;

        logWebQueue("bandwidth-standby-reprepare", {
          estimateMbps: Math.round(measuredBandwidthBps / 100_000) / 10,
          slot: slotIndex
        });
        slot.element?.pause();
        slot.ready = false;
        slot.trackBinding?.abort();
        slot.unbindEngineAudioTracks?.();
        slot.unbindEngineVideoRendition?.();
        slot.unbindEngineAudioTracks = null;
        slot.unbindEngineVideoRendition = null;
        slot.videoRendition = null;
        slot.bandwidthEstimateInherited = true;
        slot.bandwidthEstimateSeeded = measuredBandwidthBps;
        slot.media.config = webHlsMediaConfig(
          slot.audioTrackDescriptors,
          preferredAudioTrackRef.current,
          measuredBandwidthBps
        );
        slot.media.preload = "auto";
        slot.media.src = webHlsUriWithAudioPreference(
          uri,
          slot.audioTrackDescriptors,
          preferredAudioTrackRef.current
        );
        bindSlotAudioTracks(slot);
      },
      [bindSlotAudioTracks, slots]
    );
    repreparePreloadedBandwidthSlotRef.current =
      repreparePreloadedBandwidthSlot;

    const applyActivePlaybackState = useCallback(
      (slotIndex: 0 | 1) => {
        const slot = slots[slotIndex];
        const element = slots[slotIndex].element;
        if (!element) return;
        updateRenderedVideoRendition(slot);
        if (volumeIntentRef.current !== null) {
          mirrorWebQueueVolume(volumeIntentRef.current, [element]);
        }
        mirrorWebQueuePlaybackRate(playbackRateIntentRef.current, [element]);
        applyPreferredAudioTrackToSlot(slot);
        if (slot.requiresPreferredAudioBeforeAudible) slot.audioGuardMuted = true;
        element.muted =
          mutedIntentRef.current || slot.requiresPreferredAudioBeforeAudible;
        if (playingIntentRef.current) {
          if (nativeAutoplaySourceRef.current !== slots[slotIndex].uri) {
            playWithIntent(slotIndex, element);
          }
        } else {
          nativeAutoplaySourceRef.current = null;
          sourceTransitionActiveRef.current = false;
          void pauseWithDeClick(slotIndex);
        }
      },
      [
        applyPreferredAudioTrackToSlot,
        pauseWithDeClick,
        playWithIntent,
        slots,
        updateRenderedVideoRendition
      ]
    );

    const replaceRoutedAirPlaySource = useCallback(
      ({
        audioTracks,
        danceId,
        generation,
        reason,
        slotIndex,
        uri
      }: {
        audioTracks: PlaybackAudioTrack[];
        danceId: string | null;
        generation: string | number | null;
        reason: "prepared-next" | "source-change";
        slotIndex: 0 | 1;
        uri: string;
      }) => {
        const slot = slots[slotIndex];
        const element = slot.element;
        const receiverUri = webHlsUriWithAudioPreference(
          uri,
          audioTracks,
          preferredAudioTrackRef.current
        );
        if (!element || !isWebKitWirelessPlaybackActive(element)) {
          return false;
        }

        setStartedSourceUri(null);
        airPlayAudioSourceReplacementRef.current = null;
        slot.ready = false;
        slot.danceId = danceId;
        slot.uri = uri;
        slot.generation = generation;
        slot.videoRendition = null;
        publishActiveVideoRendition(slot);
        slot.audioTrackDescriptors = audioTracks;
        slot.preferredAudioTrackCommitted = null;
        slot.requiresPreferredAudioBeforeAudible = Boolean(
          hlsAudioPreferenceForId(
            slot.audioTrackDescriptors,
            preferredAudioTrackRef.current
          )
        );
        slot.audioGuardMuted = slot.requiresPreferredAudioBeforeAudible;
        slot.nativeHlsSourceOwned = true;
        nativeAutoplaySourceRef.current = null;
        replaceConnectedWebKitAirPlaySource(element, receiverUri);
        bindSlotAudioTracks(slot);
        if (playingIntentRef.current) {
          playWithIntent(slotIndex, element);
        }
        logWebQueue("airplay-source-replaced-in-place", {
          generation: webQueueGenerationLabel(generation),
          reason,
          slot: slotIndex
        });
        return true;
      },
      [bindSlotAudioTracks, playWithIntent, publishActiveVideoRendition, slots]
    );

    const promotePreparedNext = useCallback(async () => {
      clearAirPlayNaturalAdvanceTimer();
      const currentSlotIndex = activeSlotRef.current;
      const standbySlotIndex = (currentSlotIndex === 0 ? 1 : 0) as 0 | 1;
      const standby = slots[standbySlotIndex];
      const expectedUri = nextSourceUriRef.current;
      if (!expectedUri || standby.uri !== expectedUri) return false;
      const outgoingElement = slots[currentSlotIndex].element;
      const incomingElement = standby.element;
      const outgoingEnded = Boolean(outgoingElement?.ended);
      const shouldTransferPictureInPicture = Boolean(
        outgoingElement &&
          typeof document !== "undefined" &&
          document.pictureInPictureElement === outgoingElement
      );

      // `ended` has already made Video.js consider the player paused. Capture
      // the outgoing hidden state in this same event turn so no visible frame
      // of controls can slip through before the queued successor starts.
      beginActiveSourceTransition();
      logWebQueue("promote", {
        currentSlot: currentSlotIndex,
        playingIntent: playingIntentRef.current,
        standbyReady: standby.ready,
        standbyReadyState: standby.element?.readyState ?? null,
        standbySlot: standbySlotIndex
      });
      if (
        outgoingElement &&
        isWebKitWirelessPlaybackActive(outgoingElement)
      ) {
        // Safari attaches AirPlay to the HTMLVideoElement itself. Switching
        // Video.js to the prewarmed sibling would destroy that route, so keep
        // the routed element mounted and replace only its native-HLS source.
        // The hidden successor still did useful manifest/network preparation,
        // but Safari owns the final receiver handoff on this persistent host.
        remoteRouteReplacementUriRef.current = expectedUri;
        // Local Safari stays on hls.js for adaptive 4K, but an attached
        // AirPlay receiver is already consuming native HLS. Give that one
        // persistent routed element the successor directly. Mutating the
        // dormant hls.js host or fallback sibling here creates a second source
        // negotiation and the false/true route churn visible on the TV.
        const replaced = replaceRoutedAirPlaySource({
          audioTracks: standby.audioTrackDescriptors,
          danceId: standby.danceId,
          generation: standby.generation,
          reason: "prepared-next",
          slotIndex: currentSlotIndex,
          uri: expectedUri
        });
        if (replaced) {
          logWebQueue("promote-airplay-in-place", {
            currentSlot: currentSlotIndex,
            standbySlot: standbySlotIndex
          });
          return false;
        }
        remoteRouteReplacementUriRef.current = null;
      }
      // A manual Next command can cut through an arbitrary waveform. Natural
      // completion has already stopped the outgoing item and needs no delay.
      if (!outgoingEnded) {
        await pauseWithDeClick(currentSlotIndex, true);
      }
      if (
        activeSlotRef.current !== currentSlotIndex ||
        nextSourceUriRef.current !== expectedUri
      ) {
        return false;
      }
      activeSlotRef.current = standbySlotIndex;
      naturalEndEnvelopeSlotRef.current = null;
      awaitingPropAdoptionUriRef.current = expectedUri;
      setStartedSourceUri(null);
      videoRef.current = standby.element;
      setActiveSlot(standbySlotIndex);
      publishRemotePlaybackState(standby.element);
      slots[currentSlotIndex].element?.pause();
      nativeAutoplaySourceRef.current = null;
      applyActivePlaybackState(standbySlotIndex);
      if (shouldTransferPictureInPicture) {
        if (outgoingElement && incomingElement) {
          void transferPictureInPicture(outgoingElement, incomingElement);
        } else {
          exitOwnedPictureInPicture("transfer-missing-element", [outgoingElement]);
        }
      }
      return standby.ready || (standby.element?.readyState ?? 0) >= HTMLMediaElement.HAVE_FUTURE_DATA;
    }, [
      applyActivePlaybackState,
      beginActiveSourceTransition,
      clearAirPlayNaturalAdvanceTimer,
      exitOwnedPictureInPicture,
      pauseWithDeClick,
      playWithIntent,
      publishActiveVideoRendition,
      publishRemotePlaybackState,
      replaceRoutedAirPlaySource,
      slots,
      transferPictureInPicture
    ]);

    const advanceNaturalQueueBoundary = useCallback(async () => {
      if (naturalAdvanceInFlightRef.current) return;
      const outgoingUri = slots[activeSlotRef.current].uri;
      if (!outgoingUri || !nextSourceUriRef.current) return;
      naturalAdvanceInFlightRef.current = true;
      naturalAdvanceSourceUriRef.current = outgoingUri;
      try {
        const preparedSuccessorReady = await promotePreparedNext();
        callbacksRef.current.onEnded?.(preparedSuccessorReady);
      } catch (error) {
        naturalAdvanceInFlightRef.current = false;
        naturalAdvanceSourceUriRef.current = null;
        logWebQueue("natural-advance-rejected", {
          error: error instanceof Error ? `${error.name}: ${error.message}` : String(error)
        });
      }
    }, [promotePreparedNext, slots]);
    advanceNaturalQueueBoundaryRef.current = advanceNaturalQueueBoundary;

    useEffect(() => {
      if (naturalAdvanceSourceUriRef.current === sourceUri) return;
      clearAirPlayNaturalAdvanceTimer();
      naturalAdvanceInFlightRef.current = false;
      naturalAdvanceSourceUriRef.current = null;
    }, [clearAirPlayNaturalAdvanceTimer, sourceUri]);

    const attachSlotElement = useCallback(
      (slotIndex: 0 | 1, element: HTMLVideoElement | null) => {
        const slot = slots[slotIndex];
        if (slot.element === element) return;
        logWebQueue("attach", {
          activeSlot: activeSlotRef.current,
          attached: Boolean(element),
          slot: slotIndex,
          uri: slot.uri ? "set" : null
        });
        if (slot.element) {
          cancelTransportGain(slot);
          slot.pictureInPictureBinding?.abort();
          slot.pictureInPictureBinding = null;
          slot.media.detach();
        }
        slot.element = element;
        if (!element) return;
        slot.pictureInPictureBinding = new AbortController();
        const signal = slot.pictureInPictureBinding.signal;
        element.addEventListener(
          "enterpictureinpicture",
          () => {
            pictureInPictureElementRef.current = element;
            logWebQueue("pip-enter", { slot: slotIndex });
          },
          { signal }
        );
        element.addEventListener(
          "leavepictureinpicture",
          () => {
            if (pictureInPictureElementRef.current === element) {
              pictureInPictureElementRef.current = null;
            }
            logWebQueue("pip-leave", { slot: slotIndex });
          },
          { signal }
        );
        element.addEventListener(
          "webkitcurrentplaybacktargetiswirelesschanged",
          () => {
            if (slotIndex !== activeSlotRef.current) return;
            logWebQueue("airplay-route-change", {
              currentTime: element.currentTime,
              generation: slot.generation,
              paused: element.paused,
              readyState: element.readyState,
              slot: slotIndex,
              wireless: isWebKitWirelessPlaybackActive(element)
            });
            publishRemotePlaybackState(element);
            bindSlotAudioTracks(slot);
            if (isWebKitWirelessPlaybackActive(element)) {
              scheduleAirPlayAudioPreferenceSync(slot, "route-connected");
              scheduleAirPlayNaturalAdvance(element);
            } else {
              clearAirPlayAudioPreferenceSync();
              clearAirPlayNaturalAdvanceTimer();
            }
          },
          { signal }
        );
        element.addEventListener(
          "webkitplaybacktargetavailabilitychanged",
          (event) => {
            if (slotIndex !== activeSlotRef.current) return;
            const availability = (
              event as Event & { availability?: string }
            ).availability;
            publishRemotePlaybackState(
              element,
              availability === "available" ? "available" : "unavailable"
            );
          },
          { signal }
        );
        slot.media.attach(element);
        if (volumeIntentRef.current !== null) {
          mirrorWebQueueVolume(volumeIntentRef.current, [element]);
        }
        if (
          slotIndex === activeSlotRef.current &&
          slot.requiresPreferredAudioBeforeAudible
        ) {
          slot.audioGuardMuted = true;
        }
        element.muted =
          slotIndex === activeSlotRef.current
            ? mutedIntentRef.current || slot.requiresPreferredAudioBeforeAudible
            : true;
        if (
          slotIndex === activeSlotRef.current &&
          playingIntentRef.current &&
          !element.muted &&
          !hasExternalPlaybackRoute(element)
        ) {
          silenceActiveSlotRef.current(slotIndex);
        }
        mirrorWebQueuePlaybackRate(playbackRateIntentRef.current, [element]);
        if (slotIndex === activeSlotRef.current) {
          videoRef.current = element;
          publishRemotePlaybackState(element);
        }
      },
      [
        bindSlotAudioTracks,
        cancelTransportGain,
        clearAirPlayNaturalAdvanceTimer,
        clearAirPlayAudioPreferenceSync,
        publishRemotePlaybackState,
        scheduleAirPlayAudioPreferenceSync,
        scheduleAirPlayNaturalAdvance,
        slots
      ]
    );
    // A callback-ref identity change makes React invoke the old ref with null
    // even when the DOM node did not move. Detachment owns the hls.js engine,
    // so keep these DOM refs permanent across controls and AirPlay UI renders.
    const attachSlotElementRef = useRef(attachSlotElement);
    attachSlotElementRef.current = attachSlotElement;
    const attachSlotZero = useCallback(
      (element: HTMLVideoElement | null) =>
        attachSlotElementRef.current(0, element),
      []
    );
    const attachSlotOne = useCallback(
      (element: HTMLVideoElement | null) =>
        attachSlotElementRef.current(1, element),
      []
    );

    // Existing elements used for queue promotion or a controlled resume can
    // receive play intent before HLS is ready, so media-ready events re-assert
    // those imperative requests. Initial page entry intentionally stays on the
    // separate native-autoplay path above.
    const resumeIfIntended = (video: HTMLVideoElement) => {
      const activeSlot = slots[activeSlotRef.current];
      if (
        playingIntentRef.current &&
        video.paused &&
        nativeAutoplaySourceRef.current !== activeSlot.uri
      ) {
        playWithIntent(activeSlotRef.current, video);
      }
    };

    const propagatePreferredAudioTrack = useCallback(
      (id: string) => {
        preferredAudioTrackRef.current = id;
        slots.forEach((slot, rawSlotIndex) => {
          const slotIndex = rawSlotIndex as 0 | 1;
          const available = slot.audioTrackDescriptors.some(
            (descriptor) => descriptor.id === id
          );
          if (!available) return;
          const active = slotIndex === activeSlotRef.current;
          prepareAirPlayAudioSource(slot, "preference-changed");
          const wireless =
            active && isWebKitWirelessPlaybackActive(slot.element);
          const receiverTrackCount = wireless
            ? webAudioTrackArray(webKitWirelessAudioTracks(slot.element)).length
            : 0;
          if (
            active &&
            slot.element &&
            slot.uri &&
            shouldReplaceIncompleteWebKitAirPlayAudioSource({
              descriptorCount: slot.audioTrackDescriptors.length,
              trackCount: receiverTrackCount,
              wireless
            })
          ) {
            const element = slot.element;
            const position = Number.isFinite(element.currentTime)
              ? element.currentTime
              : 0;
            slot.ready = false;
            slot.preferredAudioTrackCommitted = null;
            airPlayAudioSourceReplacementRef.current = {
              element,
              generation: slot.generation,
              position,
              uri: slot.uri
            };
            logWebQueue("airplay-audio-source-replace", {
              logicalId: id,
              position: Math.round(position * 10) / 10,
              receiverTrackCount,
              slot: slotIndex
            });
            const replaced = replaceConnectedWebKitAirPlaySource(
              element,
              webHlsUriWithAudioPreference(
                slot.uri,
                slot.audioTrackDescriptors,
                id
              )
            );
            if (replaced) {
              slot.nativeHlsSourceOwned = true;
              // The rewritten manifest makes the chosen feed native HLS's
              // default. This remains deterministic even when a restored
              // Safari route exposes no receiver AudioTrack objects.
              markPreferredAudioTrackCommitted(slot, id);
              bindSlotAudioTracks(slot);
              if (playingIntentRef.current) {
                playWithIntent(slotIndex, element);
              }
              return;
            }
            airPlayAudioSourceReplacementRef.current = null;
          }
          if (wireless) {
            scheduleAirPlayAudioPreferenceSync(slot, "preference-changed");
          }
          if (
            shouldRepreparePreloadedAudioSlot({
              active,
              hasSource: Boolean(slot.uri),
              trackAvailable: available
            })
          ) {
            repreparePreloadedAudioSlot(slotIndex, id);
            return;
          }
          const activeStarted =
            active && startedSourceUri === slot.uri;
          if (!activeStarted) {
            slot.preferredAudioTrackCommitted = null;
            slot.requiresPreferredAudioBeforeAudible = true;
            if (active && slot.element) {
              slot.audioGuardMuted = true;
              slot.element.muted = true;
            }
          }
          applyPreferredAudioTrackToSlot(slot);
        });
        selectAudioTrackRef.current?.(id);
        propagatedAudioTrackRef.current = id;
      },
      [
        applyPreferredAudioTrackToSlot,
        bindSlotAudioTracks,
        markPreferredAudioTrackCommitted,
        playWithIntent,
        prepareAirPlayAudioSource,
        repreparePreloadedAudioSlot,
        scheduleAirPlayAudioPreferenceSync,
        slots,
        startedSourceUri
      ]
    );

    useLayoutEffect(() => {
      if (
        !preferredAudioTrack ||
        propagatedAudioTrackRef.current === preferredAudioTrack
      ) {
        return;
      }
      propagatePreferredAudioTrack(preferredAudioTrack);
    }, [preferredAudioTrack, propagatePreferredAudioTrack]);

    useImperativeHandle(ref, () => ({
      get availableAudioTracks() {
        return availableAudioTracksRef.current;
      },
      get currentTime() {
        return videoRef.current?.currentTime ?? 0;
      },
      get diagnostics() {
        const slotDiagnostics = (slot: WebQueueSlot): VideoSurfaceSlotDiagnostics => {
          const element = slot.element;
          const engine = slot.media.engine;
          const levelIndex = engine?.currentLevel ?? -1;
          const level = levelIndex >= 0 ? engine?.levels[levelIndex] : undefined;
          let bufferAheadSeconds = 0;
          if (element) {
            for (let index = 0; index < element.buffered.length; index += 1) {
              if (
                element.buffered.start(index) <= element.currentTime &&
                element.buffered.end(index) >= element.currentTime
              ) {
                bufferAheadSeconds = Math.max(
                  0,
                  element.buffered.end(index) - element.currentTime
                );
                break;
              }
            }
          }
          return {
            advertisedBitrateBps:
              typeof level?.bitrate === "number" ? level.bitrate : null,
            bandwidthEstimateBps: sharedWebQueueBandwidthEstimate(
              engine?.bandwidthEstimate ?? Number.NaN
            ),
            bufferAheadSeconds,
            currentTimeSeconds: element?.currentTime ?? 0,
            danceId: slot.danceId,
            mediaErrorCode: element?.error?.code ?? null,
            paused: element?.paused ?? true,
            playbackEngine: engine ? "hls.js" : "native HLS",
            readyState: element?.readyState ?? 0,
            rendition: slot.videoRendition
          };
        };
        const activeIndex = activeSlotRef.current;
        return {
          active: slotDiagnostics(slots[activeIndex]),
          standby: slotDiagnostics(slots[activeIndex === 0 ? 1 : 0])
        };
      },
      get selectedAudioTrack() {
        return selectedAudioTrackRef.current;
      },
      seek: (seconds: number) => {
        void seekWithDeClick(seconds);
      },
      setPlaybackRate: (rate: number) => {
        playbackRateIntentRef.current = mirrorWebQueuePlaybackRate(
          rate,
          slots.map((slot) => slot.element)
        );
      },
      setAudioTrack: (id: string) => {
        propagatePreferredAudioTrack(id);
      },
      advanceToNext: async () => promotePreparedNext(),
      // The browser's two persistent HLS slots are forward-prepared. Back
      // intentionally falls through to its already-fast replacement path.
      advanceToPrevious: async () => false,
      beginScrubbing: () => {
        void beginSeekWithDeClick();
      },
      endScrubbing: () => {
        // The eventual `seeked` event releases the envelope after the media
        // pipeline has actually adopted the requested timestamp.
      },
      pausePlayback: () => {
        void pauseWithDeClick(activeSlotRef.current);
      },
      prepareForSourceChange: () => {
        clearAirPlayNaturalAdvanceTimer();
        return pauseWithDeClick(activeSlotRef.current, true);
      },
      playAfterScrubbing: () => {},
      resumePlayback: () => {
        const video = videoRef.current;
        if (video) resumeIfIntended(video);
      },
      terminatePlayback: () => {
        clearAirPlayNaturalAdvanceTimer();
        exitOwnedPictureInPicture("playback-terminated");
        slots.forEach((slot) => {
          slot.element?.pause();
          slot.media.engine?.stopLoad();
          slot.uri = null;
          slot.ready = false;
        });
      },
      requestPictureInPicture: () => videoRef.current?.requestPictureInPicture(),
      requestRemotePlayback: () => {
        const slotIndex = activeSlotRef.current;
        const active = slots[slotIndex];
        const element = videoRef.current;
        const primed = Boolean(
          element &&
          active.uri &&
          primeWebKitAirPlaySource(
            element,
            webHlsUriWithAudioPreference(
              active.uri,
              active.audioTrackDescriptors,
              preferredAudioTrackRef.current
            )
          )
        );
        logWebQueue("airplay-source-primed", {
          preferredId: preferredAudioTrackRef.current,
          primed,
          slot: slotIndex
        });
        return requestWebKitPlaybackTarget(element);
      }
    }));

    useLayoutEffect(() => {
      logWebQueue("reconcile", {
        activeSlot: activeSlotRef.current,
        nextSource: nextSourceUri ? "set" : null,
        playingIntent: playingIntentRef.current,
        sourceGeneration,
        source: sourceUri ? "set" : null
      });
      let currentSlotIndex = activeSlotRef.current;
      const currentSlot = slots[currentSlotIndex];
      const otherSlotIndex = (currentSlotIndex === 0 ? 1 : 0) as 0 | 1;
      const otherSlot = slots[otherSlotIndex];

      const routeReplacementPending =
        remoteRouteReplacementUriRef.current === sourceUri;
      const replaceRoutedSource = shouldReplaceRoutedWebKitAirPlaySource({
        currentGeneration: currentSlot.generation,
        currentSrc: currentSlot.uri,
        nextGeneration: sourceGeneration,
        nextSrc: sourceUri,
        routeReplacementPending,
        wireless: isWebKitWirelessPlaybackActive(currentSlot.element)
      });

      if (!sourceUri) {
        setStartedSourceUri(null);
        configureSlot(currentSlotIndex, null, sourceGeneration);
      } else if (replaceRoutedSource) {
        beginActiveSourceTransition();
        remoteRouteReplacementUriRef.current = null;
        awaitingPropAdoptionUriRef.current = null;
        if (!replaceRoutedAirPlaySource({
          audioTracks: src?.audioTracks ?? [],
          danceId: src?.danceId ?? null,
          generation: sourceGeneration,
          reason: "source-change",
          slotIndex: currentSlotIndex,
          uri: sourceUri
        })) {
          configureSlot(currentSlotIndex, src, sourceGeneration);
        }
      } else if (currentSlot.uri === sourceUri) {
        if (remoteRouteReplacementUriRef.current === sourceUri) {
          // The routed Safari element has already begun loading this source.
          // Adopt the React queue identity without reloading it and without
          // claiming a ready frame; onFirstFrame owns that truth boundary.
          remoteRouteReplacementUriRef.current = null;
          currentSlot.generation = sourceGeneration;
          bindSlotAudioTracks(currentSlot);
        } else if (awaitingPropAdoptionUriRef.current === sourceUri) {
          awaitingPropAdoptionUriRef.current = null;
          currentSlot.generation = sourceGeneration;
          callbacksRef.current.onQueuedItemAdopted?.();
        } else if (currentSlot.generation !== sourceGeneration) {
          setStartedSourceUri(null);
          configureSlot(currentSlotIndex, src, sourceGeneration);
        }
      } else if (otherSlot.uri === sourceUri) {
        setStartedSourceUri(null);
        beginActiveSourceTransition();
        activeSlotRef.current = otherSlotIndex;
        currentSlotIndex = otherSlotIndex;
        videoRef.current = otherSlot.element;
        otherSlot.generation = sourceGeneration;
        setActiveSlot(otherSlotIndex);
      } else {
        setStartedSourceUri(null);
        configureSlot(currentSlotIndex, src, sourceGeneration);
      }

      const standbySlotIndex = (currentSlotIndex === 0 ? 1 : 0) as 0 | 1;
      // URI equality already identifies the queued item; generation only
      // distinguishes the role and must never embed the signed playback URL.
      const standbyGeneration = nextSourceUri ? "prewarm" : null;
      configureSlot(standbySlotIndex, nextSrc ?? null, standbyGeneration);
      videoRef.current = slots[currentSlotIndex].element;
      publishRemotePlaybackState(videoRef.current);
      applyActivePlaybackState(currentSlotIndex);
    }, [
      applyActivePlaybackState,
      beginActiveSourceTransition,
      bindSlotAudioTracks,
      configureSlot,
      nextSourceUri,
      nextSrc,
      publishRemotePlaybackState,
      replaceRoutedAirPlaySource,
      slots,
      src,
      sourceGeneration,
      sourceUri
    ]);

    // A new src loads through the element; initialPosition applies once
    // metadata arrives (handleLoadedMetadata).
    useEffect(() => {
      pendingSeekRef.current = initialPosition;
      firstFrameSrcRef.current = sourceUri;
      firstFrameFiredRef.current = false;
      const video = videoRef.current;
      if (
        video &&
        frameCallbackIdRef.current != null &&
        "cancelVideoFrameCallback" in video
      ) {
        video.cancelVideoFrameCallback(frameCallbackIdRef.current);
      }
      frameCallbackIdRef.current = null;
      if (video && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        queueMicrotask(() => {
          if (videoRef.current !== video) return;
          watchFirstFrame(video);
          fallbackFirstFrame();
        });
      }
      // initialPosition is intentionally only read when src changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceUri, sourceGeneration]);

    useEffect(() => {
      const video = videoRef.current;
      if (!video || !sourceUri) return;
      logWebQueue("playing-prop", {
        activeSlot: activeSlotRef.current,
        paused: video.paused,
        playing,
        readyState: video.readyState
      });
      if (playing) {
        const activeSlot = slots[activeSlotRef.current];
        if (nativeAutoplaySourceRef.current !== activeSlot.uri) {
          playWithIntent(activeSlotRef.current, video);
        }
      } else {
        nativeAutoplaySourceRef.current = null;
        sourceTransitionActiveRef.current = false;
        void pauseWithDeClick(activeSlotRef.current);
      }
    }, [pauseWithDeClick, playWithIntent, playing, slots, sourceUri]);

    useEffect(() => {
      mutedIntentRef.current = muted;
      const activeSlot = slots[activeSlotRef.current];
      if (videoRef.current) {
        if (activeSlot.requiresPreferredAudioBeforeAudible) {
          activeSlot.audioGuardMuted = true;
        }
        videoRef.current.muted =
          muted || activeSlot.requiresPreferredAudioBeforeAudible;
      }
    }, [muted, slots]);

    useEffect(() => {
      if (playbackRate === undefined) return;
      playbackRateIntentRef.current = mirrorWebQueuePlaybackRate(
        playbackRate,
        slots.map((slot) => slot.element)
      );
    }, [playbackRate, slots]);

    useEffect(() => {
      if (!thumbnailsVtt) {
        setThumbnailSprites(undefined);
        return;
      }

      const controller = new AbortController();
      setThumbnailSprites(undefined);
      void fetch(thumbnailsVtt, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Storyboard VTT failed: ${response.status}`);
          return response.text();
        })
        .then((text) => {
          if (!controller.signal.aborted) {
            const sprites = parseStoryboardVtt(text, thumbnailsVtt);
            setThumbnailSprites(sprites.length > 0 ? sprites : undefined);
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setThumbnailSprites(undefined);
        });

      return () => controller.abort();
    }, [thumbnailsVtt]);

    const fireFirstFrame = () => {
      if (
        !sourceUri ||
        firstFrameFiredRef.current ||
        firstFrameSrcRef.current !== sourceUri
      ) return;
      firstFrameFiredRef.current = true;
      callbacksRef.current.onFirstFrame?.();
    };

    const watchFirstFrame = (video: HTMLVideoElement) => {
      if (!sourceUri || firstFrameFiredRef.current || frameCallbackIdRef.current != null) return;
      if ("requestVideoFrameCallback" in video) {
        frameCallbackIdRef.current = video.requestVideoFrameCallback(() => {
          frameCallbackIdRef.current = null;
          fireFirstFrame();
        });
      }
    };

    const fallbackFirstFrame = () => {
      if (!videoRef.current || "requestVideoFrameCallback" in videoRef.current) return;
      fireFirstFrame();
    };

    // Name the native Picture-in-Picture window (and OS media controls / lock
    // screen) after the dance instead of the origin ("localhost"). Chrome
    // paints the PiP chrome itself, but it reads the label from Media Session.
    useEffect(() => {
      if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
      if (!title || !sourceArtwork) return;
      navigator.mediaSession.metadata = new MediaMetadata({
        title,
        artist: eyebrow ?? "",
        artwork: [
          {
            src: sourceArtwork,
            sizes: "640x360",
            type: "image/jpeg"
          }
        ]
      });
    }, [title, eyebrow, sourceArtwork]);

    const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      prepareAirPlayAudioSource(
        slots[activeSlotRef.current],
        "loaded-metadata"
      );
      scheduleAirPlayAudioPreferenceSync(
        slots[activeSlotRef.current],
        "loaded-metadata"
      );
      const active = slots[activeSlotRef.current];
      const replacement = airPlayAudioSourceReplacementRef.current;
      if (
        replacement &&
        replacement.element === video &&
        replacement.uri === active.uri &&
        replacement.generation === active.generation
      ) {
        if (Number.isFinite(video.duration)) {
          video.currentTime = Math.min(
            replacement.position,
            Math.max(0, video.duration - 0.25)
          );
        }
        logWebQueue("airplay-audio-source-replaced", {
          position: Math.round(video.currentTime * 10) / 10,
          slot: activeSlotRef.current
        });
        airPlayAudioSourceReplacementRef.current = null;
      } else {
        const pending = pendingSeekRef.current;
        if (
          pending &&
          pending > 0 &&
          Number.isFinite(video.duration) &&
          pending < video.duration - 1
        ) {
          video.currentTime = pending;
        }
      }
      pendingSeekRef.current = undefined;
      watchFirstFrame(video);
      resumeIfIntended(video);
      scheduleAirPlayNaturalAdvance(video);
    };

    const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;
      lastReportedTimeRef.current = video.currentTime;
      scheduleAirPlayNaturalAdvance(video);
      callbacksRef.current.onTime?.(
        video.currentTime,
        Number.isFinite(video.duration) ? video.duration : 0
      );
    };

    const handleLocalTogglePlayback = useCallback(() => {
      // This runs inside the actual click/key gesture, so Chromium is allowed
      // to resume the AudioContext before the controlled `playing` prop flips.
      void activateTransportGraph(slots[activeSlotRef.current]);
      onTogglePlay?.();
    }, [activateTransportGraph, onTogglePlay, slots]);

    const renderQueueSlot = (slotIndex: 0 | 1) => {
      const isActive = slotIndex === activeSlot;
      const slot = slots[slotIndex];
      return (
        <video
          key={slotIndex}
          ref={slotIndex === 0 ? attachSlotZero : attachSlotOne}
          aria-hidden={!isActive}
          autoPlay={isActive && playing}
          crossOrigin="anonymous"
          muted={
            isActive
              ? mutedIntentRef.current || slot.requiresPreferredAudioBeforeAudible
              : true
          }
          playsInline
          preload="auto"
          style={{
            ...(isActive ? activeQueueVideoStyle : standbyQueueVideoStyle),
            objectFit: contentFit
          }}
          onLoadedMetadata={(event) => {
            bindSlotAudioTracks(slot);
            if (slotIndex === activeSlotRef.current) handleLoadedMetadata(event);
          }}
          onLoadedData={() => {
            slot.ready = true;
            if (slotIndex === activeSlotRef.current) fallbackFirstFrame();
          }}
          onCanPlay={(event) => {
            slot.ready = true;
            logWebQueue("canplay", {
              activeSlot: activeSlotRef.current,
              paused: event.currentTarget.paused,
              playingIntent: playingIntentRef.current,
              slot: slotIndex
            });
            if (slotIndex === activeSlotRef.current) {
              prepareAirPlayAudioSource(slot, "can-play");
              scheduleAirPlayAudioPreferenceSync(slot, "can-play");
              watchFirstFrame(event.currentTarget);
              resumeIfIntended(event.currentTarget);
              recoverBlockedNativeAutoplay(slotIndex, event.currentTarget);
              scheduleAirPlayNaturalAdvance(event.currentTarget);
            }
          }}
          onVolumeChange={(event) => {
            if (slotIndex !== activeSlotRef.current) return;
            volumeIntentRef.current = mirrorWebQueueVolume(
              event.currentTarget.volume,
              slots.map((candidate) => candidate.element)
            );
            if (slot.audioGuardMuted) {
              if (
                !slot.requiresPreferredAudioBeforeAudible &&
                event.currentTarget.muted === mutedIntentRef.current
              ) {
                slot.audioGuardMuted = false;
              }
              return;
            }
            mutedIntentRef.current = event.currentTarget.muted;
            callbacksRef.current.onMutedChange?.(event.currentTarget.muted);
          }}
          onRateChange={(event) => {
            if (slotIndex !== activeSlotRef.current) return;
            const rate = mirrorWebQueuePlaybackRate(
              event.currentTarget.playbackRate,
              slots.map((candidate) => candidate.element)
            );
            playbackRateIntentRef.current = rate;
            callbacksRef.current.onPlaybackRateChange?.(rate);
            scheduleAirPlayNaturalAdvance(event.currentTarget);
          }}
          onTimeUpdate={(event) => {
            if (slotIndex === activeSlotRef.current) handleTimeUpdate(event);
          }}
          onSeeking={() => {
            if (slotIndex !== activeSlotRef.current) return;
            callbacksRef.current.onSeekStart?.(lastReportedTimeRef.current);
            clearAirPlayNaturalAdvanceTimer();
            seekEnvelopePendingRef.current = true;
            // Pointer/key capture normally starts the short ramp before the
            // seek is committed. This synchronous guard also covers seeks
            // initiated internally by Video.js or the browser.
            silenceActiveSlot(slotIndex);
            seekEnvelopeSilencedRef.current = true;
          }}
          onSeeked={(event) => {
            if (slotIndex === activeSlotRef.current) {
              lastReportedTimeRef.current = event.currentTarget.currentTime;
              callbacksRef.current.onTime?.(
                event.currentTarget.currentTime,
                event.currentTarget.duration
              );
              finishSeekWithDeClick();
              scheduleAirPlayNaturalAdvance(event.currentTarget);
            }
          }}
          onEnded={() => {
            if (slotIndex !== activeSlotRef.current) return;
            logWebQueue("ended", {
              playingIntent: playingIntentRef.current,
              slot: slotIndex
            });
            if (!nextSourceUriRef.current) {
              exitOwnedPictureInPicture("queue-ended", [slots[slotIndex].element]);
              callbacksRef.current.onEnded?.(false);
              return;
            }
            void advanceNaturalQueueBoundary();
          }}
          onError={(event) => {
            slot.ready = false;
            const active = slotIndex === activeSlotRef.current;
            const mediaError = event.currentTarget.error;
            const error = new Error(mediaError?.message || "Video.js playback failed.");
            error.name = "VideoPlaybackError";
            Sentry.captureException(error, {
              tags: { transport: "local" },
              extra: {
                danceId: slot.danceId,
                mediaErrorCode: mediaError?.code ?? null,
                queueSlot: active ? "active" : "standby"
              }
            });
            if (active) {
              nativeAutoplaySourceRef.current = null;
              callbacksRef.current.onError?.("Playback failed.");
            } else {
              console.log(`[web-queue] standby preload failed src=${slot.uri ?? "none"}`);
            }
          }}
          onPlay={(event) => {
            if (slotIndex !== activeSlotRef.current) return;
            fadeInActiveSlot(slotIndex);
            nativeAutoplaySourceRef.current = null;
            logWebQueue("play-event", {
              playingIntent: playingIntentRef.current,
              slot: slotIndex,
              transitionActive: sourceTransitionActiveRef.current
            });
            sourceTransitionActiveRef.current = false;
            setStartedSourceUri(slot.uri);
            fallbackFirstFrame();
            if (slot.element) scheduleAirPlayNaturalAdvance(slot.element);
            callbacksRef.current.onTime?.(
              event.currentTarget.currentTime,
              event.currentTarget.duration
            );
            callbacksRef.current.onPlayingChange?.(true);
          }}
          onPause={(event) => {
            if (slotIndex !== activeSlotRef.current) return;
            clearAirPlayNaturalAdvanceTimer();
            const video = event.currentTarget;
            // Chromium pauses the outgoing element shortly before dispatching
            // `ended`. That pause is the first event in an autonomous queue
            // handoff, not a user command. Start continuity here; waiting for
            // `ended` lets Video.js paint one paused/controls-visible frame and
            // leaks false back into the app's controlled playing state.
            const remaining = video.duration - video.currentTime;
            const naturalEndHandoff = isWebMediaNaturalEndPause({
              currentTime: video.currentTime,
              duration: video.duration,
              hasNext: Boolean(nextSourceUriRef.current),
              playingIntent: playingIntentRef.current
            });
            if (naturalEndHandoff) beginActiveSourceTransition();
            const ignored =
              naturalEndHandoff ||
              (sourceTransitionActiveRef.current && playingIntentRef.current);
            logWebQueue("pause-event", {
              currentTime: video.currentTime,
              duration: video.duration,
              ignored,
              naturalEndHandoff,
              playingIntent: playingIntentRef.current,
              remaining,
              slot: slotIndex,
              transitionActive: sourceTransitionActiveRef.current
            });
            if (
              naturalEndHandoff &&
              isWebKitWirelessPlaybackActive(video)
            ) {
              void advanceNaturalQueueBoundary();
            }
            if (ignored) return;
            callbacksRef.current.onTime?.(video.currentTime, video.duration);
            callbacksRef.current.onPlayingChange?.(false);
          }}
          onPlaying={(event) => {
            if (slotIndex === activeSlotRef.current) {
              scheduleAirPlayNaturalAdvance(event.currentTarget);
            }
          }}
          onWaiting={() => {
            if (slotIndex === activeSlotRef.current) {
              clearAirPlayNaturalAdvanceTimer();
            }
          }}
          onStalled={() => {
            if (slotIndex === activeSlotRef.current) {
              clearAirPlayNaturalAdvanceTimer();
            }
          }}
        />
      );
    };

    // Match HlsJsVideo's successful startup lifecycle: create the native media
    // elements only once the async playback URL exists, so the active element
    // is born with autoplay intent. Both slots then remain mounted for the
    // entire rolling queue instead of being replaced between dances.
    const media = sourceUri ? (
      <>
        <ActiveMediaBridge media={slots[activeSlot].media} />
        {renderQueueSlot(0)}
        {renderQueueSlot(1)}
      </>
    ) : null;

    // Double-click the center third → fullscreen (desktop convention). The
    // skin's own doubletap gestures own the left/right thirds (±seek), and
    // clicks on controls (buttons, sliders, menus) never count.
    const handleDoubleClick =
      showControls && onFullscreen
        ? (event: ReactMouseEvent<HTMLDivElement>) => {
            const target = event.target as HTMLElement | null;
            if (target?.closest("button, input, [role='slider'], [role='menu'], [role='menuitem']"))
              return;
            const rect = event.currentTarget.getBoundingClientRect();
            const fraction = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0.5;
            if (fraction < 1 / 3 || fraction > 2 / 3) return;
            onFullscreen();
          }
        : undefined;

    return (
      <div
        style={wrapStyle}
        // With the skin hidden (app-chrome preview) the surface itself
        // toggles play, matching native. The skin handles this when it's on.
        onClick={showControls ? undefined : handleLocalTogglePlayback}
        onDoubleClick={handleDoubleClick}
      >
        <Player.Provider>
          <AppChromeAudioTrackBridge onChange={publishAudioTrackState} />
          <ControlsContinuityBridge
            controlsPinned={controlsPinned}
            onChange={onControlsVisibilityChange}
            setSourceRevealSuppressed={setSourceRevealSuppressed}
            stableControlsVisibleRef={stableControlsVisibleRef}
            sourceGeneration={sourceGeneration}
            sourcePlaybackStarted={Boolean(
              sourceUri && startedSourceUri === sourceUri
            )}
            sourceRevealSuppressed={sourceRevealSuppressed}
          />
          {showControls ? (
            <PlayerSkin
              controlsPinned={controlsPinned}
              controlsSuppressed={sourceRevealSuppressed}
              onControlsRevealIntent={() => setSourceRevealSuppressed(false)}
              thumbnails={thumbnailSprites}
              onPrevious={onPrevious}
              onNext={onNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              isFullscreen={isFullscreen}
              onFullscreen={onFullscreen}
              onTogglePlayback={handleLocalTogglePlayback}
              onSeek={(seconds) => {
                void seekWithDeClick(seconds);
              }}
              onSeekStart={() => {
                void beginSeekWithDeClick();
              }}
            >
              {media}
            </PlayerSkin>
          ) : (
            <Player.Container>{media}</Player.Container>
          )}
          {/* Inline playback never repeats the dance title (the Now Playing
              card beneath owns identity); the overlay appears only pinned on
              the loading veil or in fullscreen, where there is no card. */}
          {showControls && (eyebrow || title) && (titlePinned || isFullscreen) ? (
            <TitleOverlay
              eyebrow={eyebrow}
              title={title}
              pinned={titlePinned || controlsPinned}
              suppressed={sourceRevealSuppressed && !controlsPinned}
              large={isFullscreen}
            />
          ) : null}
        </Player.Provider>
      </div>
    );
  }
);

const wrapStyle: CSSProperties = {
  backgroundColor: "#000",
  height: "100%",
  position: "relative",
  width: "100%"
};

const queueVideoBaseStyle: CSSProperties = {
  height: "100%",
  inset: 0,
  objectFit: "contain",
  position: "absolute",
  width: "100%"
};

const activeQueueVideoStyle: CSSProperties = {
  ...queueVideoBaseStyle,
  pointerEvents: "auto",
  visibility: "visible",
  zIndex: 1
};

const standbyQueueVideoStyle: CSSProperties = {
  ...queueVideoBaseStyle,
  pointerEvents: "none",
  visibility: "hidden",
  zIndex: 0
};

const overlayStyle: CSSProperties = {
  background:
    "linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.22) 55%, transparent)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
  gap: 2,
  inset: "0 0 auto 0",
  padding: "14px 18px 28px",
  pointerEvents: "none",
  position: "absolute",
  transition: "opacity 300ms ease",
  zIndex: 20
};

// Fullscreen: same overlay scaled for a living-room viewing distance —
// top-center, between the feed chip (left) and the cast pill (right).
const overlayLargeStyle: CSSProperties = {
  alignItems: "center",
  gap: 4,
  padding: "26px 96px 52px",
  textAlign: "center"
};

const eyebrowStyle: CSSProperties = {
  color: "rgba(243, 244, 250, 0.66)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.4,
  textTransform: "uppercase"
};

const eyebrowLargeStyle: CSSProperties = {
  fontSize: 14,
  letterSpacing: 1.8
};

const overlayTitleStyle: CSSProperties = {
  color: "#F3F4FA",
  fontSize: 17,
  fontWeight: 600,
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)"
};

const overlayTitleLargeStyle: CSSProperties = {
  fontSize: 28,
  textShadow: "0 1px 3px rgba(0, 0, 0, 0.4)"
};
